// ============================================================================
// Fiber cube cluster: an interactive Three.js scene of chrome, matte and
// frosted-glass fiber-optic cubes. The cluster floats on one side of its
// canvas, reacts to the pointer, and on command rearranges itself into icons
// or travels to the other side. Pages build their own UI around it:
//
//   import { CubeCluster } from './cube-cluster.js';
//   const cluster = new CubeCluster(canvas, { side: 'right' });
//   cluster.addEventListener('change', () => console.log(cluster.mode, cluster.side));
//   cluster.goTo('mobile', 'left');
//
// The page needs an import map for "three" and "three/addons/", or use the
// self-contained bundle (npm run build -> wordpress/.../cube-cluster.bundle.js).
//
// Methods  goTo, expand, toggle, snapTo, animate, resetView, roomFor, resize,
//          setColors, pause, resume, dispose
// Events   'ready'    the first frames are on screen
//          'change'   mode or side changed
//          'resize'   the canvas changed size
//          'press'    the cubes were clicked, or Space / Enter pressed; cancel it
//                     (preventDefault) to replace the default toggle()
//          'disturb'  the pointer started disturbing the cubes
//          'rotate'   the user turned the cluster
// Classes  the canvas gets .ready once drawn, .over while the pointer is on a
//          cube and .dragging while the cluster is being turned.
// Quality  where frames run slow it steps down by itself (options.quality);
//          cluster.fallbacks lists what it has switched to so far.
// Pausing  it stops drawing while the canvas is out of view or the tab is
//          hidden (options.pauseOffscreen); cluster.running says whether it's drawing.
// ============================================================================
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { FXAAPass } from 'three/addons/postprocessing/FXAAPass.js';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { HorizontalBlurShader } from 'three/addons/shaders/HorizontalBlurShader.js';
import { VerticalBlurShader } from 'three/addons/shaders/VerticalBlurShader.js';

// ============================================================================
// Settings
// ============================================================================
const motionQuery = matchMedia('(prefers-reduced-motion: reduce)');   // read live, so a change applies at once

// Constructor options.
const DEFAULTS = {
  container: null,                  // the element whose size the canvas follows (default: its parent)
  shape: 'home',                    // starting layout: 'home', 'expanded' or an icon
  side: 'right',                    // starting side: 'left' or 'right'
  yaw: 0,                           // starting rotation (radians)
  pitch: 0,
  keyboard: 'focus',                // 'focus': keys work while the canvas has focus; 'page': also
                                    // from the page body, and pressing the cubes focuses the canvas
  meltInto: null,                   // colour the backdrop's bottom edge fades into, to meet the page below
  glow: null,                       // colour of the fiber-optic glow (default: cyan, GLOW.color)
  glowTip: null,                    // colour of the strand tips (default: follows glow, or GLOW.tipColor)
  backdrop: null,                   // colour of the backdrop's lit centre (default: studio grey)
  pauseOffscreen: true,             // stop rendering while the canvas is out of view or the tab is hidden
  scrollEffects: false,             // turn the cluster and drag the cubes along as the window scrolls
  pixelRatio: null,                 // fixed pixel ratio; by default it follows the screen (up to 2)
                                    // and steps down when frames run slow (after the quality fallbacks)
  quality: 'auto',                  // 'auto': full quality, switching to the FALLBACK steps one at a
                                    // time where frames drop below 60 fps; 'high': never; 'low': all of them
  time: null,                       // freeze the animation clock at this many seconds (for screenshots)
  layout: {},                       // overrides for LAYOUT
  sequence: {},                     // overrides for SEQUENCE
};

// Where the cluster sits in the canvas (options.layout).
const LAYOUT = {
  slot: 0.94,                       // share of the width it may take up
  margin: 0.06,                     // NDC kept clear at the left and right edges
  anchor: 0.46,                     // furthest its centre goes from the middle, in NDC (0.46 ≈ 73% across)
  top: 0,                           // pixels kept clear at the top, e.g. for a fixed header
  compactAspect: 0,                 // at or below this width / height it is centred in a band at the top...
  compactBand: 0.5,                 // ...taking this share of the height...
  compactSlot: 0.94,                // ...and this share of the width
  turn: 0.36,                       // icons turn this far (radians) towards the middle of the canvas...
  sway: 0.08,                       // ...and sway gently around it
};

// Transitions, in seconds (options.sequence).
const SEQUENCE = {
  sweepDur: 0.78,                   // into an icon, sweeping through the spread-out cloud without stopping
  formDur: 0.51,                    // into a layout from the spread-out cloud
  formStagger: 0.018,               // per cell, from the icon's centre outwards
  travelDelay: 0.06,                // start spreading out this long before travelling
  travelDur: 0.66,
  collapseAt: 0.5,                  // form the new layout once the trip is this far along
};

const CUBE = 0.95;                  // cube edge; the grid pitch is 1
const HALF = CUBE / 2;

// Camera fitted to the reference: long lens, looking at the grid's corner.
const CAM = {
  fov: 14,
  azimuth: 45,
  elevation: 9.5,
};

// Room kept around whatever is on screen, in world units past the outermost
// cube centres. Calibrated so the assembled cluster frames like the reference;
// the bottom leaves space for the floor shadow.
const FRAME = { top: 1.6, bottom: 1.84, side: 1.27 };

const DISTURB = {
  radius: 1.3,                      // world-space falloff around the pointer
  amp: 0.55,                        // max displacement of a fully excited cube
  tilt: 0.5,                        // max tilt (radians)
  decay: 2.4,                       // excitation fade per second
  stiffness: 95,
  damping: 9,
};

const EXPAND = {
  scaleXZ: 1.58,
  scaleY: 1.42,
  jitter: 0.2,
  minGap: 1.68,                     // min center distance between expanded cubes
  maxTilt: 0.65,
  lift: 0.2,
  durOut: 0.54,
  durIn: 0.51,
  stagger: 0.018,
};

// Icons: one cube per grid cell, in a plane facing the camera.
const ICON = {
  pitch: 1.04,
  lift: 0.12,                       // balances the room above and below (see FRAME)
  glow: 1.0,                        // glass cubes glow evenly in an icon...
  core: 2.4,                        // ...and their frosted bodies light up this much
};

const SPIN = {
  perPixel: 0.0062,                 // radians of rotation per dragged pixel
  damping: 2.3,                     // inertia decay per second
  pitchLimit: 0.95,
  maxVelocity: 9,
};

const SCROLL = {
  turn: 0.16,                       // the cluster turns back and forth this far (radians) as the page scrolls
  jelly: 0.12,                      // cubes are dragged along by up to this much per viewport/s of scrolling
};

const GLOW = {
  period: 3.8,                      // idle pulse period (s)
  fiber: 14.0,                      // strands, as seen blurred through the glass
  filament: 0.75,                   // crisp additive overlay of the same strands
  tip: 5.0,
  halo: 1.25,
  glass: 0.025,
  coreLight: 3.2,
  color: new THREE.Color(0.42, 0.84, 1.0),
  tipColor: new THREE.Color(0.36, 0.86, 1.0),   // stays cyan after clipping, blooms cyan
};

const SHADOW = { size: 15, res: 256, height: 9.5, blur: 3.4, opacity: 0.8 };

// Chrome reflections, traced against the cubes themselves (see the chrome shader).
const REFLECT = {
  blur: 1.4,                        // studio mip level seen in the mirrors; also how soft reflected cube edges are
  irradiance: 7,                    // studio mip level that lights the reflected matte and glass faces
  glow: 1.4,                        // brightness of reflected glass per unit of glow level
};

// Cheaper settings the cluster switches to, in this order, where frames run
// slow (options.quality 'auto'). Each one sticks for the page's lifetime.
//   fxaa   FXAA instead of 4x multisampling
//   glass  the glass sees the scene behind it at a lower resolution
//   bloom  bloom at a lower resolution
const FALLBACK = {
  minFps: 60,                       // step down while the frame rate stays below this (5% slack for jitter)...
  after: 1,                         // ...for this many seconds
  transmissionScale: 0.5,           // glass: share of the screen size it samples the scene at (normally 0.75)
  bloomScale: 0.5,                  // bloom: share of its normal resolution
};

// ============================================================================
// Helpers
// ============================================================================
const clamp = THREE.MathUtils.clamp;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);
const easeInOutQuint = (t) => (t < 0.5 ? 16 * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 5) / 2);

// Easing for the moves between layouts and the trip between sides. Spreading
// out eases out only: the next move takes over before it has finished, and
// starting from rest there would make the cubes stop and start again.
const EASE = easeInOutQuint;
const EASE_SPREAD = easeOutQuint;

// Seeded random numbers, so every cluster is built the same way:
// rng() in [0, 1), rng.range(a, b), rng.unit() for a random direction.
function makeRandom(seed) {
  const rng = () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  rng.range = (a, b) => a + (b - a) * rng();
  rng.unit = (target = new THREE.Vector3()) => {
    const u = rng() * 2 - 1, a = rng() * Math.PI * 2, r = Math.sqrt(1 - u * u);
    return target.set(r * Math.cos(a), u, r * Math.sin(a));
  };
  return rng;
}

// Defaults overridden by whichever options are set (undefined keeps the default).
function withDefaults(defaults, options = {}) {
  const merged = { ...defaults };
  for (const [key, value] of Object.entries(options)) if (value !== undefined) merged[key] = value;
  return merged;
}

// A colour's sRGB channels, into a Vector3 (for shaders that work in sRGB).
const _rgb = { r: 0, g: 0, b: 0 };
function toSrgb(color, target) {
  const { r, g, b } = new THREE.Color(color).getRGB(_rgb, THREE.SRGBColorSpace);
  return target.set(r, g, b);
}

function sideValue(side) {
  if (side === 'left') return -1;
  if (side === 'right') return 1;
  throw new TypeError(`expected side 'left' or 'right', got ${side}`);
}

// Minimum-cost assignment (Hungarian algorithm, O(n³)) for a square cost
// matrix. Returns match[row] = column.
function assign(cost) {
  const n = cost.length;
  const u = new Float64Array(n + 1), v = new Float64Array(n + 1);
  const p = new Int32Array(n + 1), way = new Int32Array(n + 1);
  for (let i = 1; i <= n; i++) {
    p[0] = i;
    let j0 = 0;
    const minv = new Float64Array(n + 1).fill(Infinity);
    const used = new Uint8Array(n + 1);
    do {
      used[j0] = 1;
      const i0 = p[j0];
      let delta = Infinity, j1 = 0;
      for (let j = 1; j <= n; j++) {
        if (used[j]) continue;
        const cur = cost[i0 - 1][j - 1] - u[i0] - v[j];
        if (cur < minv[j]) { minv[j] = cur; way[j] = j0; }
        if (minv[j] < delta) { delta = minv[j]; j1 = j; }
      }
      for (let j = 0; j <= n; j++) {
        if (used[j]) { u[p[j]] += delta; v[j] -= delta; } else minv[j] -= delta;
      }
      j0 = j1;
    } while (p[j0] !== 0);
    do { const j1 = way[j0]; p[j0] = p[j1]; j0 = j1; } while (j0);
  }
  const match = new Int32Array(n);
  for (let j = 1; j <= n; j++) match[p[j] - 1] = j - 1;
  return match;
}

// The camera looks at the cluster from a fixed direction.
const camDir = new THREE.Vector3().setFromSphericalCoords(
  1, THREE.MathUtils.degToRad(90 - CAM.elevation), THREE.MathUtils.degToRad(CAM.azimuth));
const camRight = new THREE.Vector3(0, 1, 0).cross(camDir).normalize();
const camLevel = Math.cos(THREE.MathUtils.degToRad(CAM.elevation));   // horizontal share of camDir

// Scratch objects, reused within a single call.
const _v = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _inv = new THREE.Matrix4();
const _ray = new THREE.Ray();
const _hit = new THREE.Vector3();
const _hitPoint = new THREE.Vector3();
const _hoverLocal = new THREE.Vector3();
const _away = new THREE.Vector3();
const _toCam = new THREE.Vector3();
const _invQ = new THREE.Quaternion();
const _trail = new THREE.Vector3();
const _tweenRot = new THREE.Vector3();
const _clearColor = new THREE.Color();
const qYaw = new THREE.Quaternion();
const qPitch = new THREE.Quaternion();
const UP = new THREE.Vector3(0, 1, 0);
const LOCAL_BOX = new THREE.Box3(new THREE.Vector3(-HALF, -HALF, -HALF), new THREE.Vector3(HALF, HALF, HALF));

// ============================================================================
// Procedural textures
// ============================================================================
function canvasTexture(size, draw, colorSpace = THREE.SRGBColorSpace) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  draw(c.getContext('2d'), size);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = colorSpace;
  tex.anisotropy = 4;
  return tex;
}

// Soft falloff used to fake light scattering inside the frosted glass.
function makeGlowTexture() {
  return canvasTexture(128, (g, s) => {
    const grd = g.createRadialGradient(s / 2, s * 0.42, 2, s / 2, s / 2, s * 0.72);
    grd.addColorStop(0, '#ffffff');
    grd.addColorStop(0.4, '#7c7c7c');
    grd.addColorStop(1, '#1c1c1c');
    g.fillStyle = grd;
    g.fillRect(0, 0, s, s);
  });
}

// Fine grain for the powder-coated matte cubes.
function makeGrainTexture(rng) {
  const tex = canvasTexture(256, (g, s) => {
    const img = g.createImageData(s, s);
    for (let i = 0; i < s * s; i++) {
      const v = 128 + (rng() + rng() + rng() - 1.5) * 90;
      img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = v;
      img.data[i * 4 + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    g.globalAlpha = 0.5;
    g.filter = 'blur(0.6px)';
    g.drawImage(g.canvas, 0, 0);
  }, THREE.NoColorSpace);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

// ============================================================================
// Studio environment (drives reflections and image-based lighting). The cube
// map exists from the start, so materials can point at it; renderStudio draws
// it, and the pre-filtered copy for the PBR materials, before the first frame.
// ============================================================================
function makeStudio() {
  const cubeRT = new THREE.WebGLCubeRenderTarget(256, {
    type: THREE.HalfFloatType, generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter,
  });
  return { cubeRT, envRT: null, env: null };
}

// Draws the studio into studio.cubeRT and sets studio.env to its pre-filtered
// copy. Draws nothing if the signal aborts while the shader compiles.
async function renderStudio(renderer, studio, signal) {
  const material = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      // Key softbox sits behind camera-left, so left faces catch its edge.
      uKey: { value: new THREE.Vector3(-camRight.x - camDir.x * 0.4, 0.05, -camRight.z - camDir.z * 0.4).normalize() },
      uRight: { value: new THREE.Vector3(camRight.x, -0.05, camRight.z).normalize() },
      uStrip: { value: new THREE.Vector3(camRight.x - camDir.x * 0.16, 0.1, camRight.z - camDir.z * 0.16).normalize() },
      uFront: { value: new THREE.Vector3(camDir.x, 0, camDir.z).normalize() },
    },
    vertexShader: /* glsl */`
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */`
      varying vec3 vDir;
      uniform vec3 uKey, uRight, uStrip, uFront;
      // Rounded rectangle in azimuth/elevation space around a direction.
      float softbox(vec3 d, vec3 c, float halfAz, float halfEl, float feather) {
        float az = acos(clamp(dot(normalize(d.xz + 1e-5), normalize(c.xz + 1e-5)), -1.0, 1.0));
        float el = asin(clamp(d.y, -1.0, 1.0)) - asin(clamp(c.y, -1.0, 1.0));
        return (1.0 - smoothstep(halfAz - feather, halfAz + feather, az)) *
               (1.0 - smoothstep(halfEl - feather, halfEl + feather, abs(el)));
      }
      void main() {
        vec3 d = normalize(vDir);
        float y = d.y;
        vec3 col = vec3(0.74);                                            // walls
        col = mix(col, vec3(1.0), smoothstep(0.08, 0.75, y));             // ceiling
        col = mix(col, vec3(0.64), smoothstep(-0.05, -0.45, y));          // floor
        col = mix(col, vec3(0.4), smoothstep(-0.45, -1.0, y));
        col += softbox(d, uKey, 0.42, 0.55, 0.2) * (0.3 + 0.3 * smoothstep(-0.4, 0.5, y));
        col += 4.0 * smoothstep(0.72, 0.92, y);                          // overhead softbox
        col *= 1.0 - 0.12 * softbox(d, uRight, 0.7, 0.55, 0.3);           // slightly dimmer camera-right side
        col += 1.0 * softbox(d, uStrip, 0.03, 0.35, 0.025);               // thin strip light
        col *= 1.0 - 0.32 * softbox(d, uFront, 0.6, 0.6, 0.35);           // photographer side
        col *= vec3(0.985, 0.995, 1.01);
        gl_FragColor = vec4(col, 1.0);
      }`,
  });
  const studioScene = new THREE.Scene();
  const sphere = new THREE.Mesh(new THREE.SphereGeometry(50, 64, 32), material);
  studioScene.add(sphere);
  const cubeCamera = new THREE.CubeCamera(0.1, 100, studio.cubeRT);
  try {
    await compileFor(renderer, studioScene, cubeCamera, studio.cubeRT);
    if (signal.aborted) return;
    cubeCamera.update(renderer, studioScene);
    const pmrem = new THREE.PMREMGenerator(renderer);
    studio.envRT = pmrem.fromCubemap(studio.cubeRT.texture);
    studio.env = studio.envRT.texture;
    pmrem.dispose();
  } finally {
    sphere.geometry.dispose();
    material.dispose();
  }
}

// Compiles the programs for drawing these objects into a target (null for
// the screen), off the main thread where the GPU allows it
// (KHR_parallel_shader_compile). A program depends on its target: tone
// mapping and the sRGB conversion only happen on the way to the screen.
function compileFor(renderer, object, camera, target) {
  const previous = renderer.getRenderTarget();
  renderer.setRenderTarget(target);
  const job = renderer.compileAsync(object, camera);      // picks up the target right away
  renderer.setRenderTarget(previous);
  return job;
}

// Puts full-screen materials on a bare triangle with FullScreenQuad's
// attributes (position, uv), the other thing a program depends on, so they
// can be compiled before first use. Frees the triangle once that's done.
function compileQuads(renderer, materials, camera, target) {
  const triangle = new THREE.BufferGeometry();
  triangle.setAttribute('position', new THREE.Float32BufferAttribute([-1, 3, 0, -1, -1, 0, 3, -1, 0], 3));
  triangle.setAttribute('uv', new THREE.Float32BufferAttribute([0, 2, 0, 0, 2, 0], 2));
  const group = new THREE.Group();
  for (const m of materials) group.add(new THREE.Mesh(triangle, m));
  return compileFor(renderer, group, camera, target).finally(() => triangle.dispose());
}

// Lets the browser handle input and paint between the steps of a long job.
const yieldToMain = () => (globalThis.scheduler?.yield ? scheduler.yield() : new Promise((resolve) => setTimeout(resolve)));

// ============================================================================
// Backdrop: seamless studio sweep drawn as a full-screen gradient, using sRGB
// values sampled from the reference. Its pool of light follows the cluster a
// little, and the bottom edge can melt into the page colour. Its colours are
// set by CubeCluster.setColors (options.backdrop, options.meltInto).
// ============================================================================
const BACKDROP_TONE = 0.942;        // sRGB grey at the centre of the light pool

function makeBackdrop() {
  const backdrop = new THREE.Mesh(
    new THREE.PlaneGeometry(2, 2),
    new THREE.ShaderMaterial({
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uAspect: { value: 1 },
        uCenter: { value: 0 },                                     // NDC x of the light pool
        uLift: { value: 0 },                                       // NDC y of the light pool
        uTone: { value: new THREE.Vector3().setScalar(BACKDROP_TONE) },   // lit centre, sRGB
        uPage: { value: new THREE.Vector3() },                     // page colour below, sRGB
        uMelt: { value: 0 },
      },
      vertexShader: /* glsl */`
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }`,
      fragmentShader: /* glsl */`
        varying vec2 vUv;
        uniform float uAspect;
        uniform float uCenter;
        uniform float uLift;
        uniform vec3 uTone;
        uniform vec3 uPage;
        uniform float uMelt;
        vec3 toLinear(vec3 c) { return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c)); }
        float hash(vec2 p) {
          vec3 p3 = fract(vec3(p.xyx) * 0.1031);
          p3 += dot(p3, p3.yzx + 33.33);
          return fract((p3.x + p3.y) * p3.z);
        }
        void main() {
          vec2 p = vec2((vUv.x - 0.5 - uCenter * 0.5) * uAspect, vUv.y - 0.5 - uLift * 0.5);
          float r = length(p * vec2(0.82, 1.0) - vec2(0.0, 0.06));
          float v = ${BACKDROP_TONE} - 0.032 * smoothstep(0.12, 0.95, r);
          v += 0.014 * smoothstep(0.45, 0.0, vUv.y);                  // floor bounce
          vec3 col = uTone * (v / ${BACKDROP_TONE});                  // the grey sweep, tinted
          col = mix(col, uPage, uMelt * smoothstep(0.18, 0.0, vUv.y));   // melt into the page below
          vec3 lin = toLinear(col);
          lin += (hash(gl_FragCoord.xy) - 0.5) * (3.0 / 255.0);       // dither against banding
          gl_FragColor = vec4(lin, 1.0);
        }`,
    }),
  );
  backdrop.frustumCulled = false;
  backdrop.renderOrder = -1000;
  return backdrop;
}

// ============================================================================
// Layout, reconstructed from the reference render.
// The camera looks at the voxel grid from a 45° corner, so a screen column is
// x − z and depth is x + z. Each vertical column of cubes slides by its own
// offset, which is what gives the cluster its jagged, stepped silhouette.
// [x, z, [[y, material], ...]]   g = frosted fiber glass, c = chrome, d = matte
// Cubes hidden in the reference view are mixed too, so every side of the
// cluster looks as rich as the front when it is rotated.
// ============================================================================
const COLUMNS = [
  [ 0,  0, [[3.98, 'g'], [2.98, 'c'], [1.98, 'g'], [0.98, 'c'], [-0.02, 'd'], [-1.02, 'd']]],
  [ 0,  1, [[3.05, 'c'], [2.05, 'g'], [1.05, 'g'], [0.05, 'd'], [-0.95, 'g']]],
  [ 1,  1, [[2.94, 'g'], [1.91, 'g'], [0.84, 'g'], [-0.18, 'd'], [-1.20, 'd']]],
  [ 1,  0, [[3.24, 'd'], [2.265, 'g'], [1.29, 'c'], [0.29, 'd'], [-0.71, 'd']]],
  [ 1, -1, [[2.45, 'd'], [1.45, 'g'], [0.45, 'g'], [-0.55, 'c']]],
  [-1,  3, [[2.67, 'c'], [1.60, 'g']]],
  [-1,  2, [[2.63, 'g'], [1.62, 'g'], [0.55, 'g']]],
  [-1,  1, [[2.65, 'd'], [1.65, 'g'], [0.65, 'c'], [-0.35, 'g']]],
  [ 0,  2, [[1.85, 'c'], [0.83, 'g'], [-0.15, 'd'], [-1.18, 'c']]],
  [ 1,  2, [[0.86, 'c'], [-0.12, 'd'], [-1.13, 'g']]],
  [ 2,  2, [[0.40, 'g'], [-1.28, 'g'], [-2.26, 'c']]],
  [ 2,  1, [[1.23, 'c'], [0.20, 'g'], [-1.40, 'd']]],
  [ 2,  0, [[2.17, 'd'], [1.17, 'c'], [0.17, 'g'], [-0.83, 'd']]],
  [ 3,  1, [[1.48, 'd'], [0.40, 'c'], [-0.58, 'd']]],
  [ 3,  0, [[0.41, 'c']]],
  [ 2, -1, [[0.92, 'c'], [-0.08, 'g']]],
  [ 3, -1, [[0.41, 'g'], [-0.71, 'g']]],
  [ 0, -1, [[2.37, 'g'], [1.37, 'd'], [0.37, 'c'], [-0.63, 'g']]],
  [-1,  0, [[2.57, 'd'], [1.57, 'g'], [0.57, 'c'], [-0.43, 'g']]],
];
const PIVOT = new THREE.Vector3(1, 0.74, 1);     // centre of the cluster's bounds
const TRUNK_COLUMNS = new Set(['1,1', '0,1']);   // glass stacks with a vertical light trunk

// Icons, drawn as pixel grids with one cube per cell: G = frosted glass
// (glows), C = chrome, M = matte, # = chrome or matte, * = any cube. Cubes an
// icon doesn't use line up in a second layer behind it.
const ICONS = {
  mobile: [                         // phone: glowing screen, speaker and home button
    '.CCCCC.',
    'M..M..M',
    'MGGGGGM',
    'MGGGGGM',
    'MGGGGGM',
    'MGGGGGM',
    'MGGGGGM',
    'MGGGGGM',
    'M..M..M',
    '.CCCCC.',
  ],
  maintenance: [                    // gear: glowing ring, polished tooth tips
    '....CCC....',
    '.C#.###.#C.',
    '.#GGGGGGG#.',
    '..GG...GG..',
    'C#G.....G#C',
    'C#G.....G#C',
    'C#G.....G#C',
    '..GG...GG..',
    '.#GGGGGGG#.',
    '.C#.###.#C.',
    '....CCC....',
  ],
  web: [                            // globe: glowing rim, meridian and equator
    '...GGGGG...',
    '..G.M.M.G..',
    '.G.M...M.G.',
    'G..M...M..G',
    'G..M...M..G',
    'GCCMCCCMCCG',
    'G..M...M..G',
    'G..M...M..G',
    '.G.M...M.G.',
    '..G.M.M.G..',
    '...GGGGG...',
  ],
  ai: [                             // chip: glowing die, chrome pins
    '..C.C.C..',
    '.CMMCMMC.',
    'CMGGGGGMC',
    '.MGGGGGM.',
    'CMGGGGGMC',
    '.MGGGGGM.',
    'CMGGGGGMC',
    '.CMMCMMC.',
    '..C.C.C..',
  ],
};

// ============================================================================
// Materials
// ============================================================================
// Chrome uses a custom mirror shader. Every cube is a box, so each chrome pixel
// traces its reflection ray against all of them (passed in as one uniform
// array) and shades what it hits; rays that miss see the floor or the studio.
// Exact every frame, sharp at any resolution, and no extra render passes.
const chromeVertex = /* glsl */`
  varying vec3 vWorldPos;
  varying vec3 vLocalPos;
  varying vec3 vLocalNormal;
  void main() {
    vLocalPos = position;
    vLocalNormal = normal;
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }`;
const chromeFragment = /* glsl */`
  uniform mat4 modelMatrix;
  uniform samplerCube envMap;          // the studio
  uniform sampler2D glowMap;
  // Two per cube: centre.xyz, half-size (0 = hidden) | rotation quat.xyz (w >= 0),
  // then glass glow level (>= 0), or -1 for matte, -2 for chrome.
  uniform vec4 boxes[2 * BOX_COUNT];
  uniform int boxCount;                // 0 skips the trace (the glass transmission pass)
  uniform int selfIndex;
  uniform vec4 floorInfo;              // centre x, height, centre z
  uniform vec3 tint;
  uniform vec3 glowColor;
  uniform vec3 matteColor;
  uniform vec3 glassColor;
  uniform float blur;
  uniform float irradiance;
  uniform float bulge;
  varying vec3 vWorldPos;
  varying vec3 vLocalPos;
  varying vec3 vLocalNormal;

  const float TEXEL = 1.5707963 / 256.0;   // one studio texel at mip 0, in radians

  vec3 rotate(vec4 q, vec3 v) {
    vec3 t = 2.0 * cross(q.xyz, v);
    return v + q.w * t + cross(q.xyz, t);
  }
  vec4 boxQuat(int i) {
    vec3 v = boxes[2 * i + 1].xyz;
    return vec4(v, sqrt(max(1.0 - dot(v, v), 0.0)));
  }
  // No zero components, so the slab test never computes inf * 0.
  vec3 nonZero(vec3 d) {
    return mix(d, vec3(1e-6), lessThan(abs(d), vec3(1e-6)));
  }
  // Specular share of a dielectric (F0 0.04) for image-based light (Karis' fit).
  float dielectricSpecular(float rough, float nv) {
    vec4 r = rough * vec4(-1.0, -0.0275, -0.572, 0.022) + vec4(1.0, 0.0425, 1.04, -0.04);
    float a = min(r.x * r.x, exp2(-9.28 * nv)) * r.x + r.y;
    vec2 ab = vec2(-1.04, 1.04) * a + r.zw;
    return 0.04 * ab.x + ab.y;
  }

  // What the ray sees on box i: lit like the cubes themselves, roughly.
  vec3 shadeBox(int i, vec3 ro, vec3 rd, float lod) {
    vec4 b = boxes[2 * i];
    float code = boxes[2 * i + 1].w;
    vec4 q = boxQuat(i);
    vec4 qi = vec4(-q.xyz, q.w);
    vec3 lo = rotate(qi, ro - b.xyz);
    vec3 ld = nonZero(rotate(qi, rd));
    vec3 m = 1.0 / ld;
    vec3 tA = -m * lo - abs(m) * b.w;
    float tN = max(max(tA.x, tA.y), tA.z);
    vec3 nL = -sign(ld) * step(tA.yzx, tA) * step(tA.zxy, tA);   // the face it enters through
    vec3 p = clamp(lo + ld * tN, -b.w, b.w);
    vec3 nW = rotate(q, nL);
    vec3 rW = reflect(rd, nW);
    if (code < -1.5) return textureLod(envMap, rW, lod + 1.0).rgb * tint;    // chrome
    float nv = clamp(dot(nW, -rd), 0.0, 1.0);
    vec3 light = textureLod(envMap, nW, irradiance).rgb;
    if (code < 0.0) {                                                        // matte
      return matteColor * light + dielectricSpecular(0.66, nv) * textureLod(envMap, rW, irradiance - 2.0).rgb;
    }
    // Frosted glass, glowing like the fiber core behind it (same face layout as
    // BoxGeometry's UVs: the glow is symmetric across, so only v matters).
    vec2 f = abs(nL.y) > 0.5 ? vec2(p.x, -p.z * nL.y) : vec2(abs(nL.x) > 0.5 ? p.z : p.x, p.y);
    vec3 glow = textureLod(glowMap, 0.5 + 0.5 * f / b.w, 0.0).rgb;
    return glassColor * light + dielectricSpecular(0.5, nv) * textureLod(envMap, rW, lod + 3.0).rgb +
      glowColor * glow * code;
  }

  // The cubes as seen along the ray, over the background: the two nearest,
  // with how much of the pixel each covers.
  vec3 traceCubes(vec3 ro, vec3 R, float lod, float cone, float spot, vec3 bg) {
    float t0 = 1e9, t1 = 1e9, a0 = 0.0, a1 = 0.0;
    int i0 = -1, i1 = -1;
    for (int i = 0; i < boxCount; i++) {
      if (i == selfIndex) continue;
      vec4 b = boxes[2 * i];
      vec3 oc = b.xyz - ro;
      float tc = dot(oc, R);
      float rB = 1.7320508 * b.w;
      float reach = rB + cone * max(tc, 0.0) + spot;       // near misses still soften the edge
      if (tc < -rB || dot(oc, oc) - tc * tc > reach * reach) continue;
      if (a0 > 0.996 && tc - rB > t0) continue;            // hidden behind a solid hit
      vec4 q = boxQuat(i);
      vec3 lo = rotate(vec4(-q.xyz, q.w), -oc);
      vec3 ld = nonZero(rotate(vec4(-q.xyz, q.w), R));
      vec3 m = 1.0 / ld;
      vec3 c = -m * lo, k = abs(m) * b.w;
      vec3 tA = c - k, tB = c + k;                         // where the ray enters and leaves each slab
      float tN = max(max(tA.x, tA.y), tA.z);
      if (tN <= 1e-4) continue;
      // Signed distance from the ray to the box outline (> 0 inside): each
      // entry/exit pair of different slabs is one of its six outline edges.
      vec3 s = abs(ld);
      vec3 w = s * s.yzx * inversesqrt(s * s + s.yzx * s.yzx);
      vec3 e = min((tB.yzx - tA) * w, (tB.zxy - tA) * w.zxy);
      float d = min(min(e.x, e.y), e.z);
      float a = clamp(0.5 + 0.5 * d / (cone * tN + spot), 0.0, 1.0);
      if (a < 0.004) continue;
      if (tN < t0) { t1 = t0; a1 = a0; i1 = i0; t0 = tN; a0 = a; i0 = i; }
      else if (tN < t1) { t1 = tN; a1 = a; i1 = i; }
    }
    vec3 col = bg;
    if (i1 >= 0 && a0 < 0.996) col = mix(col, shadeBox(i1, ro, R, lod), a1);
    if (i0 >= 0) col = mix(col, shadeBox(i0, ro, R, lod), a0);
    return col;
  }

  void main() {
    // Faces are very slightly convex, so each one sweeps across the studio
    // and picks up a gradient instead of a single flat colour.
    vec3 n = normalize(vLocalNormal);
    vec3 an = abs(n);
    float faceW = smoothstep(0.85, 0.995, max(an.x, max(an.y, an.z)));
    vec3 axis = step(vec3(0.7), an);
    vec3 lateral = vLocalPos * (1.0 - axis);
    vec3 N = normalize(mat3(modelMatrix) * normalize(n + lateral * bulge * faceW));
    vec3 V = normalize(vWorldPos - cameraPosition);
    vec3 R = reflect(V, N);
    // Blur where the normal changes fast (bevels) to stop reflections sparkling.
    // Derivatives all come first: none are defined inside the loop below.
    float aa = clamp(length(fwidth(N)) * 10.0, 0.0, 3.0);
    float lod = blur + aa;
    // Reflected cube edges are as soft as the studio at this blur level, and
    // never sharper than a pixel.
    float cone = max(0.5 * TEXEL * exp2(lod), 0.5 * length(fwidth(R)));
    float spot = 0.5 * length(fwidth(vWorldPos)) + 1e-5;

    // Behind the cubes: the studio floor under the cluster, then the studio.
    vec3 ro = vWorldPos;
    vec3 env = textureLod(envMap, R, lod).rgb;
    if (R.y < -1e-4) {
      float tf = (floorInfo.y - ro.y) / R.y;
      float r = length(ro.xz + R.xz * tf - floorInfo.xz);
      vec3 ground = vec3(mix(0.9, 0.3, smoothstep(5.0, 26.0, r)));   // a pool of light, falling off
      env = mix(ground, env, smoothstep(60.0, 80.0, tf));
    }
    env = traceCubes(ro, R, lod, cone, spot, env);

    float fres = pow(1.0 - clamp(dot(N, -V), 0.0, 1.0), 5.0);
    vec3 col = env * mix(tint, vec3(1.0), fres);
    col /= 1.0 + max(col - 1.0, 0.0) * 0.6;       // soft knee: glints stay crisp, no blooming
    gl_FragColor = vec4(col, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }`;
// `shared` holds the uniforms every chrome cube reads (the boxes, the floor,
// colours), as the same objects, so filling them once a frame feeds all of them.
function makeChromeMaterial(shared, index) {
  return new THREE.ShaderMaterial({
    defines: { BOX_COUNT: shared.boxes.value.length / 8 },
    uniforms: {
      ...shared,
      tint: { value: new THREE.Color(0.71, 0.725, 0.74) },
      bulge: { value: 0.2 },
      boxCount: { value: shared.boxes.value.length / 8 },
      selfIndex: { value: index },
    },
    vertexShader: chromeVertex,
    fragmentShader: chromeFragment,
  });
}

// Seen through frosted transmission the strands blur into haze, so an additive
// copy is drawn on top as crisp filaments. Vertices slide towards the camera
// along their view ray: same pixels, but they clear their own glass face while
// cubes further in front still hide them.
const pullTowardCamera = /* glsl */`
  vec4 pulledView(vec3 p, float bias) {
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    float len = length(mv.xyz);
    mv.xyz *= max(len - bias, 0.01) / len;
    return mv;
  }`;

// The shared materials; glass and the fiber materials are templates that each
// glass cube clones.
function makeMaterials(textures) {
  const matte = new THREE.MeshStandardMaterial({
    color: 0x2e2f32,
    roughness: 0.66,
    metalness: 0,
    roughnessMap: textures.grain,
    bumpMap: textures.grain,
    bumpScale: 0.45,
    envMapIntensity: 1.05,
  });

  const glass = new THREE.MeshPhysicalMaterial({
    color: 0x8c9196,
    metalness: 0,
    roughness: 0.3,
    transmission: 0.8,
    thickness: 1.1,
    ior: 1.4,
    attenuationColor: new THREE.Color(0x7c8186),
    attenuationDistance: 1.1,
    specularIntensity: 0.75,
    envMapIntensity: 0.75,
    emissive: GLOW.color.clone(),
    emissiveMap: textures.glow,
    emissiveIntensity: 0,
  });

  // Fiber strands: emissive tubes with small light packets travelling to the tips.
  const fiber = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uIntensity: { value: 1 },
      uColor: { value: GLOW.color.clone() },
    },
    vertexShader: /* glsl */`
      attribute float aSeed;
      attribute float aGain;
      varying float vAlong;
      varying float vSeed;
      varying float vGain;
      void main() {
        vAlong = uv.x;
        vSeed = aSeed;
        vGain = aGain;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */`
      uniform float uTime;
      uniform float uIntensity;
      uniform vec3 uColor;
      varying float vAlong;
      varying float vSeed;
      varying float vGain;
      void main() {
        float w = fract(vAlong * 0.85 - uTime * 0.32 + vSeed);
        float packet = smoothstep(0.0, 0.06, w) * (1.0 - smoothstep(0.06, 0.28, w));
        vec3 col = uColor * uIntensity * vGain * (0.4 + 0.8 * vAlong + 1.7 * packet);
        // Exposed strands read as near-white filaments in the reference.
        col = mix(col, vec3(dot(col, vec3(0.33))), 0.45 * (1.0 - vGain));
        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });

  const filament = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uIntensity: { value: 1 },
      uColor: { value: GLOW.color.clone() },
    },
    vertexShader: /* glsl */`
      attribute float aSeed;
      attribute float aGain;
      varying float vAlong;
      varying float vSeed;
      varying float vGain;
      varying float vFacing;
      ${pullTowardCamera}
      void main() {
        vAlong = uv.x;
        vSeed = aSeed;
        vGain = aGain;
        // Inflate the tube and fade towards its silhouette for a soft glow line.
        vec4 mv = pulledView(position + normal * 0.011, 0.75);
        vFacing = abs(dot(normalize(normalMatrix * normal), normalize(-mv.xyz)));
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */`
      uniform float uTime;
      uniform float uIntensity;
      uniform vec3 uColor;
      varying float vAlong;
      varying float vSeed;
      varying float vGain;
      varying float vFacing;
      void main() {
        if (vGain < 0.99) discard;                   // exposed strands need no overlay
        float w = fract(vAlong * 0.85 - uTime * 0.32 + vSeed);
        float packet = smoothstep(0.0, 0.06, w) * (1.0 - smoothstep(0.06, 0.28, w));
        float fade = smoothstep(0.0, 0.25, vAlong);  // fade in from the entry point
        float core = pow(clamp(vFacing, 0.0, 1.0), 2.5);   // pow(<0) is NaN, and bloom would spread it
        gl_FragColor = vec4(uColor * uIntensity * fade * core * (0.35 + 0.45 * vAlong + 2.4 * packet), 1.0);
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  // Soft halo sprite around every fiber tip.
  const halo = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: GLOW.tipColor.clone() },
      uIntensity: { value: 1 },
      uSize: { value: 0.19 },
      uScale: { value: 1000 },
    },
    vertexShader: /* glsl */`
      uniform float uSize;
      uniform float uScale;
      ${pullTowardCamera}
      void main() {
        vec4 mv = pulledView(position, 0.12);
        gl_Position = projectionMatrix * mv;
        gl_PointSize = uSize * uScale / -mv.z;
      }`,
    fragmentShader: /* glsl */`
      uniform vec3 uColor;
      uniform float uIntensity;
      void main() {
        vec2 p = gl_PointCoord * 2.0 - 1.0;
        float r2 = dot(p, p);
        float a = exp(-r2 * 5.0) * (1.0 - smoothstep(0.6, 1.0, r2));
        gl_FragColor = vec4(uColor * uIntensity * a, 1.0);
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  return { matte, glass, fiber, filament, halo };
}

// Frosted tops catch the overhead softbox and read lighter than the sides.
function patchGlass(material) {
  material.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <emissivemap_fragment>',
      `#include <emissivemap_fragment>
      {
        vec3 worldN = inverseTransformDirection(normal, viewMatrix);
        totalEmissiveRadiance += vec3(0.085, 0.09, 0.095) * smoothstep(0.55, 0.95, worldN.y);
      }`);
  };
  return material;
}

// ============================================================================
// Geometry
// ============================================================================
const FACES = [
  { n: new THREE.Vector3( 1, 0, 0), u: new THREE.Vector3(0, 0, -1), v: new THREE.Vector3(0, 1, 0), w: 1.0 },
  { n: new THREE.Vector3(-1, 0, 0), u: new THREE.Vector3(0, 0, 1), v: new THREE.Vector3(0, 1, 0), w: 0.3 },
  { n: new THREE.Vector3(0, 1, 0), u: new THREE.Vector3(1, 0, 0), v: new THREE.Vector3(0, 0, -1), w: 0.35 },
  { n: new THREE.Vector3(0, -1, 0), u: new THREE.Vector3(1, 0, 0), v: new THREE.Vector3(0, 0, 1), w: 0.12 },
  { n: new THREE.Vector3(0, 0, 1), u: new THREE.Vector3(1, 0, 0), v: new THREE.Vector3(0, 1, 0), w: 1.0 },
  { n: new THREE.Vector3(0, 0, -1), u: new THREE.Vector3(-1, 0, 0), v: new THREE.Vector3(0, 1, 0), w: 0.3 },
];

// gain < 1 for strands that are seen directly rather than through frosted glass
function tube(points, radius, seed, gain = 1) {
  const curve = new THREE.CatmullRomCurve3(points, false, 'centripetal');
  const geo = new THREE.TubeGeometry(curve, 32, radius, 5, false);
  const count = geo.attributes.position.count;
  geo.setAttribute('aSeed', new THREE.BufferAttribute(new Float32Array(count).fill(seed), 1));
  geo.setAttribute('aGain', new THREE.BufferAttribute(new Float32Array(count).fill(gain), 1));
  return geo;
}

// Plans the fiber-optic strands and tip beads living inside one glass cube:
// the strands' tube() arguments and the tip positions. All the random choices
// happen here, in the cluster's seeded order; buildFibers makes the geometry
// later, which is the slow part.
function planFibers(cube, rng, { trunk = false, bundleUp = 0 } = {}) {
  const strands = [];
  const tips = [];

  // Light enters through the face that points towards the cluster core.
  const toCore = new THREE.Vector3(-cube.home.x, -cube.home.y * 0.6, -cube.home.z);
  let entry = FACES[3];
  if (bundleUp > 0) entry = FACES[2];
  else if (toCore.lengthSq() > 0.25) {
    let best = -Infinity;
    for (const f of FACES) {
      const s = f.n.dot(toCore) + (f === FACES[3] ? 0.35 : 0);
      if (s > best) { best = s; entry = f; }
    }
  }
  const entryPoint = entry.n.clone().multiplyScalar(HALF - 0.01)
    .addScaledVector(entry.u, rng.range(-0.1, 0.1))
    .addScaledVector(entry.v, rng.range(-0.1, 0.1));

  const candidates = FACES.filter((f) => f !== entry);
  const totalW = candidates.reduce((s, f) => s + f.w, 0);
  const pickFace = () => {
    let r = rng() * totalW;
    for (const f of candidates) { r -= f.w; if (r <= 0) return f; }
    return candidates[0];
  };

  const tipCount = 2 + Math.floor(rng() * 3);
  for (let i = 0; i < tipCount; i++) {
    const f = pickFace();
    const upBias = f.n.y === 0 ? 0.08 : 0;
    const tip = f.n.clone().multiplyScalar(HALF + 0.002)
      .addScaledVector(f.u, rng.range(-0.3, 0.3))
      .addScaledVector(f.v, rng.range(-0.26, 0.3) + upBias);
    tips.push(tip);
    const inward = entryPoint.clone().addScaledVector(entry.n, -0.2);
    const mid = entryPoint.clone().lerp(tip, 0.55).multiplyScalar(0.55)
      .add(rng.unit().multiplyScalar(0.07));
    const approach = tip.clone().addScaledVector(f.n, -0.16);
    const end = tip.clone().addScaledVector(f.n, -0.015);
    strands.push([[entryPoint.clone(), inward, mid, approach, end], 0.008, rng()]);
  }

  if (trunk) {
    // Vertical strands running straight through stacked glass cubes.
    for (let i = 0; i < 5; i++) {
      const a = new THREE.Vector3(rng.range(-0.09, 0.09), -HALF + 0.03, rng.range(-0.09, 0.09));
      const b = new THREE.Vector3(rng.range(-0.12, 0.12), HALF - 0.03, rng.range(-0.12, 0.12));
      const m = a.clone().lerp(b, 0.5).add(new THREE.Vector3(rng.range(-0.06, 0.06), 0, rng.range(-0.06, 0.06)));
      strands.push([[a, m, b], 0.009, rng()]);
    }
  }

  if (bundleUp > 0) {
    // Exposed bundle bridging the gap to the cube above (visible in the reference).
    for (let i = 0; i < 11; i++) {
      const a = new THREE.Vector3(rng.range(-0.16, 0.16), HALF - 0.02, rng.range(-0.16, 0.16));
      const b = new THREE.Vector3(rng.range(-0.34, 0.34), HALF + bundleUp + 0.04, rng.range(-0.34, 0.34));
      const m1 = a.clone().lerp(b, 0.33).add(new THREE.Vector3(rng.range(-0.06, 0.06), 0, rng.range(-0.06, 0.06)));
      const m2 = a.clone().lerp(b, 0.7).add(new THREE.Vector3(rng.range(-0.08, 0.08), 0, rng.range(-0.08, 0.08)));
      strands.push([[a, m1, m2, b], 0.0062, rng(), 0.13]);
    }
  }

  return { strands, tips };
}

// The strand and tip geometry for a plan from planFibers.
function buildFibers({ strands, tips }, tipGeometry) {
  const tubes = strands.map((args) => tube(...args));
  const tipGeos = tips.map((p) => tipGeometry.clone().translate(p.x, p.y, p.z));
  const merged = { strands: mergeGeometries(tubes), tips: mergeGeometries(tipGeos) };
  for (const g of [...tubes, ...tipGeos]) g.dispose();
  return merged;
}

// ============================================================================
// Cubes. Each cube is an independent, animatable object:
//   cube.group    its THREE.Group; the built-in animation drives its transform
//   cube.custom   { position, rotation, scale } layered on top of the built-in
//                 animation: set it directly, or tween it with cluster.animate()
// ============================================================================
class Cube {
  constructor(index, x, y, z, type, rng) {
    this.index = index;
    this.type = type;
    this.home = new THREE.Vector3(x, y, z).sub(PIVOT);
    this.rest = this.home.clone();                  // pose + custom layer, before disturbance
    this.group = new THREE.Group();
    this.group.name = `cube-${index}`;
    this.group.position.copy(this.home);

    // Base pose, tweened between layouts (see "Layouts").
    this.pose = { pos: this.home.clone(), quat: new THREE.Quaternion(), drift: 0, spread: 0, icon: 0 };
    this.queue = [];                                // upcoming moves, by start time
    this.move = null;                               // the move in progress
    this.iconPhase = 0;                             // pulse phase while part of an icon

    this.offset = new THREE.Vector3();
    this.offsetVel = new THREE.Vector3();
    this.tilt = 0;
    this.tiltVel = 0;
    this.tiltAxis = rng.unit();
    this.disturbDir = this.home.clone().normalize();
    this.excite = 0;

    this.explodeOffset = new THREE.Vector3();
    this.explodeQuat = new THREE.Quaternion();
    this.seed = rng();

    // Cluster-local offsets layered on top of everything else.
    this.custom = { position: new THREE.Vector3(), rotation: new THREE.Euler(), scale: 1 };
  }
}

// ============================================================================
// Expanded layout: scale out from the core, jitter, then relax so no two
// cubes overlap once they are spread and tumbling.
// ============================================================================
function computeExplodedLayout(cubes, rng) {
  const targets = cubes.map((c) => c.home.clone()
    .multiply(new THREE.Vector3(EXPAND.scaleXZ, EXPAND.scaleY, EXPAND.scaleXZ))
    .add(rng.unit().multiplyScalar(EXPAND.jitter * rng()))
    .add(new THREE.Vector3(0, EXPAND.lift, 0)));
  const tmp = new THREE.Vector3();
  for (let iter = 0; iter < 40; iter++) {
    for (let i = 0; i < targets.length; i++) {
      for (let j = i + 1; j < targets.length; j++) {
        tmp.subVectors(targets[j], targets[i]);
        const d = tmp.length();
        if (d < EXPAND.minGap && d > 1e-5) {
          tmp.multiplyScalar((EXPAND.minGap - d) / d * 0.5);
          targets[i].sub(tmp);
          targets[j].add(tmp);
        }
      }
    }
  }
  cubes.forEach((c, i) => {
    c.explodeOffset.subVectors(targets[i], c.home);
    const angle = rng.range(0.25, EXPAND.maxTilt) * (rng() < 0.5 ? -1 : 1);
    c.explodeQuat.setFromAxisAngle(rng.unit(), angle);
  });
}

// ============================================================================
// Layouts: the poses cubes move between, in the cluster's frame.
//   home       the assembled cluster from the reference
//   expanded   spread out and tumbling
//   icons      the pixel-art ICONS, in a plane facing the camera
// Each layout also knows how much room it needs on screen.
// ============================================================================
function measure(points) {
  let top = -Infinity, bottom = Infinity, side = 0;
  for (const p of points) {
    top = Math.max(top, p.y);
    bottom = Math.min(bottom, p.y);
    side = Math.max(side, Math.abs(p.dot(camRight)));
  }
  return { halfH: Math.max(top + FRAME.top, FRAME.bottom - bottom), halfW: side + FRAME.side };
}

const ICON_NORMAL = new THREE.Vector3(camDir.x, 0, camDir.z).normalize();     // towards the camera
const ICON_QUAT = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), ICON_NORMAL);
const WANTS = { G: 'g', C: 'c', M: 'd', '#': 'cd', '*': 'gcd' };

function buildIcon(rows, cubes, expandedLayout) {
  const cells = [];
  rows.forEach((row, r) => [...row].forEach((ch, q) => {
    if (!WANTS[ch]) return;
    const u = (q - (row.length - 1) / 2) * ICON.pitch;
    const v = ((rows.length - 1) / 2 - r) * ICON.pitch;
    cells.push({ want: WANTS[ch], u, v, depth: 0 });
  }));
  cells.length = Math.min(cells.length, cubes.length);
  // Spare cubes stack up behind the middle of the icon.
  const front = [...cells].sort((a, b) => Math.hypot(a.u, a.v) - Math.hypot(b.u, b.v));
  for (let i = 0; cells.length < cubes.length; i++) {
    const f = front[i % front.length];
    cells.push({ want: WANTS['*'], u: f.u, v: f.v, depth: 1 + Math.floor(i / front.length) });
  }
  const targets = cells.map((cell) => new THREE.Vector3()
    .addScaledVector(camRight, cell.u)
    .addScaledVector(ICON_NORMAL, -cell.depth * ICON.pitch)
    .setY(cell.v + ICON.lift));
  // Send every cube to a cell that wants its material, keeping the total
  // flight from the expanded layout as short as possible.
  const cost = cubes.map((c, i) => targets.map((p, j) =>
    expandedLayout.poses[i].pos.distanceToSquared(p) + (cells[j].want.includes(c.type) ? 0 : 1e4)));
  const cellOf = assign(cost);
  return {
    poses: cubes.map((c, i) => ({ pos: targets[cellOf[i]], quat: ICON_QUAT })),
    order: cubes.map((c, i) => Math.hypot(cells[cellOf[i]].u, cells[cellOf[i]].v) / ICON.pitch),
    drift: 0, spread: 0.6, icon: 1,
  };
}

// ============================================================================
// Animation helpers
// ============================================================================
// A curve from a to b that passes through v halfway (k = 0.5), where it is
// still heading from a towards b: a move through a waypoint never stops there.
const through = (a, v, b, k) => a * (1 - k) * (1 - 2 * k) + b * k * (2 * k - 1) + v * 4 * k * (1 - k);

// Advances a cube along its queue of moves. A move starts from wherever the
// cube is when its start time arrives, so sequences chain and interrupt smoothly.
function updatePose(c, t) {
  while (c.queue.length && t >= c.queue[0].start) {
    const m = c.queue.shift();
    m.from = { pos: c.pose.pos.clone(), quat: c.pose.quat.clone(), drift: c.pose.drift, spread: c.pose.spread, icon: c.pose.icon };
    if (m.to.icon > 0 && !m.via) c.iconPhase = m.to.phase;
    c.move = m;
  }
  const m = c.move;
  if (!m) return false;
  const u = clamp((t - m.start) / m.dur, 0, 1);
  const k = m.ease(u);
  const kc = clamp(k, 0, 1);
  const pose = c.pose;
  const { from, via, to } = m;
  if (via) {
    // Sweep through the waypoint; its tumble peaks there and eases back out.
    pose.pos.copy(from.pos).multiplyScalar(through(1, 0, 0, kc))
      .addScaledVector(via.pos, through(0, 1, 0, kc))
      .addScaledVector(to.pos, through(0, 0, 1, kc));
    pose.quat.slerpQuaternions(from.quat, to.quat, kc).slerp(via.quat, 4 * kc * (1 - kc));
    pose.drift = through(from.drift, via.drift, to.drift, kc);
    pose.spread = through(from.spread, via.spread, to.spread, kc);
    pose.icon = through(from.icon, via.icon, to.icon, kc);
    // Switch the glow's ripple to the new icon's while no icon is showing.
    if (kc >= 0.5 && to.icon > 0) c.iconPhase = to.phase;
  } else {
    pose.pos.lerpVectors(from.pos, to.pos, k);
    pose.quat.slerpQuaternions(from.quat, to.quat, kc);
    pose.drift = from.drift + (to.drift - from.drift) * kc;
    pose.spread = from.spread + (to.spread - from.spread) * kc;
    pose.icon = from.icon + (to.icon - from.icon) * kc;
  }
  if (m.arc) pose.pos.addScaledVector(m.arc, Math.sin(Math.PI * u));
  if (u >= 1) {
    c.move = null;
    m.onArrive?.(c);
  }
  return true;
}

// Little click as a cube locks back into the cluster...
function lockIntoPlace(c) {
  c.offsetVel.addScaledVector(_v.copy(c.home).normalize(), 0.55);
  c.excite = Math.max(c.excite, 0.3);
}
// ...and a small knock back as it lands in an icon.
function settleIntoIcon(c) {
  c.offsetVel.addScaledVector(ICON_NORMAL, -1.2);
}

const staggerFrom = (origin) => (c) =>
  (origin ? c.pose.pos.distanceTo(origin) : c.pose.pos.length()) * EXPAND.stagger + c.seed * 0.03;

// Idle light pulse: a slow, eased swell between 0.4 and 1.35.
function pulseAt(t, phase) {
  const s = 0.5 + 0.5 * Math.sin((t / GLOW.period) * Math.PI * 2 - phase);
  return 0.4 + 0.95 * s * s * (3 - 2 * s);
}

const toVector3 = (v) => (Array.isArray(v) ? new THREE.Vector3().fromArray(v) : new THREE.Vector3(v.x || 0, v.y || 0, v.z || 0));

// Bloom that can drop to a lower resolution. Its blur kernels shrink with it
// (in texels, which grow), so the glow keeps its size on screen.
const BLOOM_KERNELS = [6, 10, 14, 18, 22];      // UnrealBloomPass's own, per mip (three r186)
class ScalableBloomPass extends UnrealBloomPass {
  scale = 1;
  #size = null;
  #spare = null;

  setSize(width, height) {
    this.#size = [width, height];
    super.setSize(Math.max(1, Math.round(width * this.scale)), Math.max(1, Math.round(height * this.scale)));
  }

  // The blur materials for another scale, built once (so they can be compiled ahead of time).
  prepareScale(scale) {
    this.#spare ??= BLOOM_KERNELS.map((k) => this._getSeparableBlurMaterial(Math.max(1, Math.round(k * scale))));
    return this.#spare;
  }

  setScale(scale) {
    const materials = this.prepareScale(scale);
    for (const m of this.separableBlurMaterials) m.dispose();
    this.separableBlurMaterials = materials;
    this.#spare = null;
    this.scale = scale;
    if (this.#size) this.setSize(...this.#size);
  }

  dispose() {
    super.dispose();
    this.#spare?.forEach((m) => m.dispose());
  }
}

// ============================================================================
// The cluster
// ============================================================================
export class CubeCluster extends EventTarget {
  // The icons it can form, besides 'home' (the assembled cluster) and 'expanded'.
  static shapes = Object.keys(ICONS);

  canvas;
  renderer;
  scene;
  camera;
  group;                            // the cluster; its children are the cubes' groups
  cubes = [];

  #options;
  #layout;
  #seq;
  #container;
  #fixedPixelRatio;
  #pixelRatio;
  #textures;
  #studio;
  #backdrop;
  #materials;
  #geometries;
  #glassCubes = [];
  #coreLights = [];
  #glow = { color: GLOW.color.clone(), tip: GLOW.tipColor.clone() };   // this cluster's glow colours
  #colors = { glow: null, glowTip: null, backdrop: null, meltInto: null };   // as last set (setColors)
  #layouts;
  #reflect;                         // uniforms shared by the chrome cubes (see the chrome shader)
  #shadow;
  #floorBase = 0;
  #floorY = 0;
  #composer;
  #passes;
  #steps;                           // FALLBACK steps not taken yet, in order
  #fallbacks = [];                  // ...and those taken
  #compact = false;
  #band = { half: 1, centre: 0 };   // the rows the cluster is framed in, in NDC
  #raycaster = new THREE.Raycaster();

  #pointer = {
    ndc: new THREE.Vector2(9, 9), px: new THREE.Vector2(), lastPx: new THREE.Vector2(),
    inside: false, speed: 0, type: 'mouse', pokeTime: 0,
  };
  #drag = { down: false, active: false, id: -1, startX: 0, startY: 0, lastX: 0, lastY: 0, startTime: 0, samples: [] };
  #spin = { yaw: 0, pitch: 0, vYaw: 0, vPitch: 0, resetting: false };
  #state = { time: 0, spread: 0 };
  #view = {
    target: 'home',                 // where the cubes are headed: 'home', 'expanded' or an icon
    frame: 'home',                  // the layout the camera frames
    side: 1,                        // 1 = right, -1 = left
    turnSide: 1,                    // eased copy of side, for the icon turn
    turn: 0,                        // 0..1, how far an icon has turned towards the middle
    distance: 40, tanH: 0.3, room: 0,
  };
  #anchor = { value: 1, from: 1, to: 1, start: 0, dur: 1, velocity: 0 };   // -1 left .. 1 right
  #fit;                             // bounds the camera is easing towards
  #scroll = { y: 0, vel: 0, turn: 0 };
  #timeline = [];
  #tweens = new Set();
  #returnTo = 'home';               // where toggle() brings expanded cubes back to
  #hovering = false;

  #abort = new AbortController();
  #resizeObserver;
  #visibility;                      // IntersectionObserver that pauses the cluster while out of view
  #holds = new Set();               // why the animation is paused: 'offscreen', 'hidden', or pause()'s reason
  #started = false;                 // true once the shaders are compiled and frames may run
  #raf = 0;
  #last = 0;
  #frameCount = 0;
  #avgFrame = 1 / 60;
  #slowTime = 0;                    // seconds below 40 fps, for the resolution
  #fallbackSlow = 0;                // seconds below FALLBACK.minFps, for the next fallback
  #settleUntil = 0;                 // frame count until which a new fallback settles in
  #ready = false;
  #disposed = false;
  #tick = (now) => this.#frame(now);

  // Throws where WebGL isn't available.
  constructor(canvas, options = {}) {
    super();
    this.#options = withDefaults(DEFAULTS, options);
    if (!['auto', 'high', 'low'].includes(this.#options.quality)) {
      throw new TypeError(`expected quality 'auto', 'high' or 'low', got ${this.#options.quality}`);
    }
    this.#layout = withDefaults(LAYOUT, options.layout);
    this.#seq = withDefaults(SEQUENCE, options.sequence);
    this.canvas = canvas;
    this.#container = this.#options.container ?? canvas.parentElement;
    const rng = makeRandom(20260927);

    // Renderer
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance', stencil: false });
    this.renderer = renderer;
    const { pixelRatio } = this.#options;
    this.#fixedPixelRatio = pixelRatio != null ? clamp(pixelRatio, 0.5, 3) : null;
    this.#pixelRatio = this.#fixedPixelRatio ?? Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, this.#container.clientWidth), h = Math.max(1, this.#container.clientHeight);
    renderer.setPixelRatio(this.#pixelRatio);
    renderer.setSize(w, h, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.LinearToneMapping;      // keeps sampled reference colours exact
    renderer.toneMappingExposure = 1;
    renderer.transmissionResolutionScale = 0.75;
    // Where multisampling renders straight into tile memory it costs next to nothing.
    const cheapMsaa = renderer.extensions.has('WEBGL_multisampled_render_to_texture');
    this.#steps = ['fxaa', 'glass', 'bloom'].filter((step) => step !== 'fxaa' || !cheapMsaa);
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(CAM.fov, w / h, 1, 400);

    // Environment
    this.#textures = { glow: makeGlowTexture(), grain: makeGrainTexture(rng) };
    this.#studio = makeStudio();                           // drawn in #start; scene.environment set there
    this.#backdrop = makeBackdrop();
    this.scene.add(this.#backdrop);

    // Cubes, layouts, shadow and post-processing
    this.#createCubes(rng);
    this.#createLayouts();
    this.#createShadow();
    this.#createComposer();
    const { glow, glowTip, backdrop, meltInto } = this.#options;
    this.setColors({ glow, glowTip, backdrop, meltInto });
    if (this.#options.quality === 'low') while (this.#steps.length) this.#applyFallback(this.#steps.shift());

    this.#bindEvents();
    this.resize();

    const view = this.#view;
    view.room = this.#layout.anchor;
    this.#fit = { ...this.#layouts.home.bounds };
    this.#scroll.y = scrollY;
    this.#spin.yaw = this.#options.yaw;
    this.#spin.pitch = this.#options.pitch;
    this.snapTo(this.#options.shape, this.#options.side);
    this.#start();
  }

  // ==========================================================================
  // Public API
  // ==========================================================================
  // Where the cubes are headed: 'home', 'expanded' or an icon name.
  get mode() { return this.#view.target; }
  // The side of the canvas the cluster is on (or heading to): 'left' or 'right'.
  get side() { return this.#view.side > 0 ? 'right' : 'left'; }
  // True when the canvas is tall enough that the cluster is centred in a band at the top.
  get compact() { return this.#compact; }
  // True once the first frames are on screen.
  get ready() { return this.#ready; }
  // The cheaper settings switched to so far: 'fxaa', 'glass', 'bloom' (see FALLBACK).
  get fallbacks() { return [...this.#fallbacks]; }
  get shapes() { return CubeCluster.shapes; }

  // Goes to a layout ('home' or an icon) on a side ('left' or 'right'; by
  // default the current one). The cubes sweep through the spread-out cloud
  // into the new layout without stopping there; changing sides, they are most
  // spread out mid-trip. Going back to the cluster on the same side, they fly
  // straight home. origin: a point in the cluster's frame they ripple out from.
  goTo(name, side, origin = null) {
    if (!Object.hasOwn(this.#layouts, name) || name === 'expanded') {
      throw new TypeError(`goTo: expected 'home' or one of ${CubeCluster.shapes.join(', ')}, got ${name}`);
    }
    const layout = this.#layouts[name];
    const view = this.#view;
    const to = side == null ? view.side : sideValue(side);
    const changeSide = to !== view.side;
    if (!changeSide && view.target === name) return;
    this.#returnTo = name;
    // Centred in compact mode, so change sides on the spot, ready for when it widens.
    const travel = changeSide && !this.#compact;
    if (changeSide && !travel) {
      view.turnSide = to;
      Object.assign(this.#anchor, { value: to, from: to, to });
    }
    view.side = to;
    if (view.target !== name || travel) {
      const spreadOut = view.frame === 'expanded';
      this.#timeline.length = 0;
      view.target = name;
      this.resetView();
      const seq = this.#seq;
      const cloud = spreadOut ? null : this.#layouts.expanded;
      if (travel) {
        const delay = spreadOut ? 0 : seq.travelDelay;
        const formAt = delay + seq.travelDur * seq.collapseAt;
        view.frame = 'expanded';
        Object.assign(this.#anchor, { from: this.#anchor.value, to, start: this.#state.time + delay, dur: seq.travelDur });
        this.#later(formAt, () => { view.frame = name; });
        if (cloud) this.#formLayout(layout, { via: cloud, dur: 2 * formAt });
        else this.#formLayout(layout, { delay: formAt });
      } else {
        view.frame = name;
        const sweep = cloud && layout !== this.#layouts.home;
        this.#formLayout(layout, sweep ? { via: cloud, dur: seq.sweepDur } : { origin });
      }
    }
    this.#emit('change');
  }

  // Spreads the cubes out where they are.
  expand(origin = null) {
    this.#timeline.length = 0;
    this.#view.target = this.#view.frame = 'expanded';
    this.#expandCubes(origin);
    this.#emit('change');
  }

  // Spreads the cubes out, or brings them back to the layout they came from.
  // This is what pressing the cubes does, unless the 'press' event is cancelled.
  toggle(origin = null) {
    if (this.#view.target === 'expanded') this.goTo(this.#returnTo, null, origin);
    else this.expand(origin);
  }

  // Jumps straight to a layout ('home', 'expanded' or an icon), and optionally
  // a side, without animating.
  snapTo(name, side) {
    if (!Object.hasOwn(this.#layouts, name)) {
      throw new TypeError(`snapTo: expected 'home', 'expanded' or one of ${CubeCluster.shapes.join(', ')}, got ${name}`);
    }
    const layout = this.#layouts[name];
    this.#timeline.length = 0;
    this.cubes.forEach((c, i) => {
      c.queue.length = 0;
      c.move = null;
      c.pose.pos.copy(layout.poses[i].pos);
      c.pose.quat.copy(layout.poses[i].quat);
      c.pose.drift = layout.drift;
      c.pose.spread = layout.spread;
      c.pose.icon = layout.icon;
      if (layout.order) c.iconPhase = layout.order[i] * 0.35;
    });
    const view = this.#view;
    view.target = view.frame = name;
    view.turn = layout.icon;
    if (name !== 'expanded') this.#returnTo = name;
    if (side != null) {
      const s = sideValue(side);
      view.side = view.turnSide = s;
      Object.assign(this.#anchor, { value: s, from: s, to: s });
    }
    this.#emit('change');
  }

  // Tweens a cube's custom layer (see Cube), e.g. from the console:
  //   await cubeCluster.animate(5, { position: [0, 0.6, 0], rotation: [0, Math.PI / 2, 0] });
  //   cubeCluster.animate(5, { position: [0, 0, 0], rotation: [0, 0, 0] }, { duration: 1.2 });
  // Each call resolves when its tween ends; a new tween on a cube replaces its running one.
  animate(target, to = {}, { duration = 0.8, delay = 0, ease = easeOutCubic } = {}) {
    const cube = typeof target === 'number' ? this.cubes[target] : target;
    if (!cube?.custom) throw new TypeError('animate: expected a cube or a cube index');
    for (const tw of this.#tweens) if (tw.cube === cube) { this.#tweens.delete(tw); tw.resolve(cube); }
    return new Promise((resolve) => {
      this.#tweens.add({ cube, to, from: null, start: this.#state.time + delay, duration: Math.max(duration, 1e-3), ease, resolve });
    });
  }

  // Eases the user's rotation back to the reference view.
  resetView() {
    const spin = this.#spin;
    spin.vYaw = spin.vPitch = 0;
    spin.yaw = Math.atan2(Math.sin(spin.yaw), Math.cos(spin.yaw));   // don't unwind extra turns
    spin.resetting = true;
  }

  // Changes colours on the fly: { glow, glowTip, backdrop, meltInto }, each a
  // CSS colour string, hex number or THREE.Color. Leave one out to keep it,
  // or pass null to go back to the default.
  setColors(colors = {}) {
    const c = this.#colors = withDefaults(this.#colors, colors);
    const glow = c.glow != null ? new THREE.Color(c.glow) : GLOW.color;
    const tip = c.glowTip != null ? new THREE.Color(c.glowTip) : c.glow != null ? glow : GLOW.tipColor;
    this.#glow.color.copy(glow);
    this.#glow.tip.copy(tip);
    for (const cube of this.#glassCubes) {
      cube.material.emissive.copy(glow);
      cube.fiberMaterial.uniforms.uColor.value.copy(glow);
      cube.filamentMaterial.uniforms.uColor.value.copy(glow);
      cube.haloMaterial.uniforms.uColor.value.copy(tip);
    }
    this.#reflect.glowColor.value.copy(glow);
    for (const light of this.#coreLights) light.color.copy(glow);
    const u = this.#backdrop.material.uniforms;
    if (c.backdrop != null) toSrgb(c.backdrop, u.uTone.value);
    else u.uTone.value.setScalar(BACKDROP_TONE);
    u.uMelt.value = c.meltInto != null ? 1 : 0;
    if (c.meltInto != null) toSrgb(c.meltInto, u.uPage.value);
  }

  // Stops and restarts the animation. Pauses stack by reason, so a pause() of
  // yours and the automatic ones (off screen, hidden tab) never undo each other.
  pause(reason = 'api') {
    this.#holds.add(reason);
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
  }

  resume(reason = 'api') {
    this.#holds.delete(reason);
    this.#run();
  }

  // True while frames are being drawn.
  get running() { return this.#started && !this.#disposed && !this.#holds.size; }

  // How far (NDC) a layout's centre can sit from the middle of the canvas at
  // its current size, e.g. to tell whether changing sides is worth offering.
  roomFor(name = 'home') {
    return this.#frameFor(this.#layouts[name].bounds).room;
  }

  // Follows the container's size. Called automatically on resize.
  resize() {
    const w = Math.max(1, this.#container.clientWidth), h = Math.max(1, this.#container.clientHeight);
    const { renderer, camera } = this;
    renderer.setPixelRatio(this.#pixelRatio);
    renderer.setSize(w, h, false);
    this.#composer.setPixelRatio(this.#pixelRatio);
    this.#composer.setSize(w, h);
    camera.aspect = w / h;
    // The picture shifts so the cluster is framed below any space kept at the
    // top: in the rest of the canvas, or in compact mode in a band at the top.
    const layout = this.#layout;
    this.#compact = w / h <= layout.compactAspect;
    const bar = (2 * layout.top) / h;
    const band = this.#band;
    band.half = this.#compact ? layout.compactBand : 1 - bar / 2;
    band.centre = 1 - bar - band.half;
    camera.setViewOffset(w, h, 0, (band.centre * h) / 2, w, h);
    const uniforms = this.#backdrop.material.uniforms;
    uniforms.uAspect.value = w / h;
    uniforms.uLift.value = band.centre;
    // Point sprites are sized in pixels: world size * this / view depth.
    const haloScale = (h * this.#pixelRatio) / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)));
    for (const c of this.#glassCubes) c.haloMaterial.uniforms.uScale.value = haloScale;
    this.#emit('resize');
  }

  // Stops the animation, removes the listeners and frees the GPU resources.
  dispose() {
    if (this.#disposed) return;
    this.#disposed = true;
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    this.#abort.abort();
    this.#resizeObserver.disconnect();
    this.#visibility?.disconnect();
    const freed = new Set();
    const free = (thing) => {
      if (!thing?.dispose || freed.has(thing)) return;
      freed.add(thing);
      thing.dispose();
    };
    for (const root of [this.scene, this.#shadow.scene]) {
      root.traverse((o) => {
        if (o.isInstancedMesh) free(o);
        free(o.geometry);
        for (const m of [o.material].flat()) free(m);
      });
    }
    Object.values(this.#textures).forEach(free);
    Object.values(this.#geometries).forEach(free);
    Object.values(this.#materials).forEach(free);
    const sh = this.#shadow;
    [sh.rt, sh.blurRT, sh.caster, sh.blurH, sh.blurV, sh.quad].forEach(free);
    [this.#studio.cubeRT, this.#studio.envRT].forEach(free);
    this.#passes.forEach(free);
    free(this.#composer);
    this.renderer.dispose();
    this.renderer.forceContextLoss();                      // hand the WebGL context back now, not at GC
  }

  // ==========================================================================
  // Building
  // ==========================================================================
  #createCubes(rng) {
    this.#materials = makeMaterials(this.#textures);
    this.#geometries = {
      g: new RoundedBoxGeometry(CUBE, CUBE, CUBE, 3, 0.035),
      c: new RoundedBoxGeometry(CUBE, CUBE, CUBE, 3, 0.03),
      d: new RoundedBoxGeometry(CUBE, CUBE, CUBE, 4, 0.06),
      tip: new THREE.IcosahedronGeometry(0.022, 1),
      proxy: new THREE.BoxGeometry(CUBE, CUBE, CUBE),
    };
    this.group = new THREE.Group();
    this.scene.add(this.group);
    const { cubes } = this;

    const count = COLUMNS.reduce((n, [, , stack]) => n + stack.length, 0);
    this.#reflect = {
      envMap: { value: this.#studio.cubeRT.texture },
      glowMap: { value: this.#textures.glow },
      boxes: { value: new Float32Array(8 * count) },
      floorInfo: { value: new THREE.Vector4() },
      glowColor: { value: GLOW.color.clone() },               // per cluster (setColors)
      matteColor: { value: new THREE.Color(0x2e2f32) },   // as the matte material
      glassColor: { value: new THREE.Color(0x55606b) },
      blur: { value: REFLECT.blur },
      irradiance: { value: REFLECT.irradiance },
    };
    if (2 * count + 24 > this.renderer.capabilities.maxFragmentUniforms) {
      console.warn('CubeCluster: too many cubes for the chrome reflections on this GPU');
    }

    for (const [x, z, stack] of COLUMNS) {
      for (const [y, type] of stack) {
        const cube = new Cube(cubes.length, x, y, z, type, rng);
        cube.column = `${x},${z}`;
        let material;
        if (type === 'c') {
          material = makeChromeMaterial(this.#reflect, cube.index);
        } else if (type === 'g') {
          material = patchGlass(this.#materials.glass.clone());
          this.#glassCubes.push(cube);
        } else {
          material = this.#materials.matte;
        }
        cube.material = material;
        cube.mesh = new THREE.Mesh(this.#geometries[type], material);
        if (type === 'c') {
          // Glass sees the chrome through heavy frosting, so skip the trace when
          // the scene is drawn for it; upload the change even if nothing else did.
          cube.mesh.onBeforeRender = (renderer) => {
            const target = renderer.getRenderTarget();
            material.uniforms.boxCount.value = target === null || target === this.#composer.readBuffer ? count : 0;
            material.uniformsNeedUpdate = true;
          };
        }
        cube.group.add(cube.mesh);
        this.group.add(cube.group);
        cubes.push(cube);
      }
    }

    // Light spilling out of the fiber core onto neighbouring cubes.
    this.#coreLights = [new THREE.Vector3(0, 1.2, 0), new THREE.Vector3(0.6, -0.9, 0.6)].map((p) => {
      const light = new THREE.PointLight(GLOW.color.clone(), 0, 0, 2);
      light.position.copy(p);
      this.group.add(light);
      return light;
    });

    // Glow level falls off with distance from the core, like the reference.
    const { fiber, filament, halo } = this.#materials;
    for (const cube of this.#glassCubes) {
      const above = cubes.find((o) => o.column === cube.column && o.home.y > cube.home.y &&
        o.home.y - cube.home.y > 1.5 && o.home.y - cube.home.y < 2.0);
      const bundleUp = cube.column === '2,2' && above ? above.home.y - cube.home.y - CUBE : 0;
      // The strand and tip geometry follows in #start (#buildFibers).
      cube.fiberPlan = planFibers(cube, rng, { trunk: TRUNK_COLUMNS.has(cube.column), bundleUp });
      cube.fiberMaterial = fiber.clone();
      cube.filamentMaterial = filament.clone();
      cube.haloMaterial = halo.clone();
      cube.tipMaterial = new THREE.MeshBasicMaterial({ color: GLOW.tipColor.clone() });
      const pending = new THREE.BufferGeometry();
      cube.strands = new THREE.Mesh(pending, cube.fiberMaterial);
      cube.filaments = new THREE.Mesh(pending, cube.filamentMaterial);
      cube.tips = new THREE.Mesh(new THREE.BufferGeometry(), cube.tipMaterial);
      cube.halos = new THREE.Points(new THREE.BufferGeometry().setFromPoints(cube.fiberPlan.tips), cube.haloMaterial);
      cube.filaments.renderOrder = cube.halos.renderOrder = 2;
      cube.group.add(cube.strands, cube.filaments, cube.tips, cube.halos);
      const radial = Math.hypot(cube.home.x, cube.home.z * 1.0);
      cube.isCore = TRUNK_COLUMNS.has(cube.column);
      cube.glowLevel = clamp(1.15 - radial * 0.16, 0.55, 1.15) * (cube.isCore ? 1.35 : 1);
      cube.reflectGlow = 0;                        // current glow level, for the chrome reflections
      // The whole cluster breathes together, with a soft ripple outward and upward.
      cube.pulsePhase = radial * 0.45 + cube.home.y * 0.18 + cube.seed * 0.3;
    }

    computeExplodedLayout(cubes, rng);
  }

  #createLayouts() {
    const { cubes } = this;
    const home = {
      poses: cubes.map((c) => ({ pos: c.home.clone(), quat: new THREE.Quaternion() })),
      drift: 0, spread: 0, icon: 0,
    };
    const expanded = {
      poses: cubes.map((c) => ({ pos: c.home.clone().add(c.explodeOffset), quat: c.explodeQuat.clone() })),
      drift: 1, spread: 1, icon: 0,
    };
    const icons = Object.fromEntries(Object.entries(ICONS).map(([name, rows]) => [name, buildIcon(rows, cubes, expanded)]));
    this.#layouts = { home, expanded, ...icons };
    for (const layout of Object.values(this.#layouts)) layout.bounds = measure(layout.poses.map((p) => p.pos));
  }

  // Contact shadow (rendered from below, blurred, projected on an invisible floor)
  #createShadow() {
    const rt = new THREE.WebGLRenderTarget(SHADOW.res, SHADOW.res);
    const blurRT = new THREE.WebGLRenderTarget(SHADOW.res, SHADOW.res);
    rt.texture.generateMipmaps = blurRT.texture.generateMipmaps = false;

    const group = new THREE.Group();
    this.scene.add(group);
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(SHADOW.size, SHADOW.size).rotateX(Math.PI / 2),
      new THREE.MeshBasicMaterial({
        alphaMap: rt.texture, color: 0x15212b, transparent: true, depthWrite: false, opacity: SHADOW.opacity,
      }),
    );
    plane.scale.y = -1;
    plane.renderOrder = 1;
    group.add(plane);
    const camera = new THREE.OrthographicCamera(
      -SHADOW.size / 2, SHADOW.size / 2, SHADOW.size / 2, -SHADOW.size / 2, 0, SHADOW.height);
    camera.rotation.x = Math.PI / 2;
    group.add(camera);

    const caster = new THREE.ShaderMaterial({
      uniforms: { uFloor: { value: 0 }, uHeight: { value: SHADOW.height } },
      vertexShader: /* glsl */`
        uniform float uFloor;
        varying float vH;
        void main() {
          vec4 wp = modelMatrix *
          #ifdef USE_INSTANCING
            instanceMatrix *
          #endif
            vec4(position, 1.0);
          vH = wp.y - uFloor;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }`,
      fragmentShader: /* glsl */`
        uniform float uHeight;
        varying float vH;
        void main() {
          float a = clamp(1.0 - vH / uHeight, 0.0, 1.0);
          gl_FragColor = vec4(vec3(a * a), 1.0);        // read back through alphaMap (green)
        }`,
    });
    const blurH = new THREE.ShaderMaterial({
      ...HorizontalBlurShader, uniforms: THREE.UniformsUtils.clone(HorizontalBlurShader.uniforms),
    });
    const blurV = new THREE.ShaderMaterial({
      ...VerticalBlurShader, uniforms: THREE.UniformsUtils.clone(VerticalBlurShader.uniforms),
    });
    blurH.depthTest = blurV.depthTest = false;

    // Plain boxes standing in for the cubes, seen from below.
    const casters = new THREE.InstancedMesh(this.#geometries.proxy, caster, this.cubes.length);
    casters.frustumCulled = false;
    casters.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    const scene = new THREE.Scene();
    scene.add(casters);

    this.#shadow = {
      rt, blurRT, group, plane, camera, caster, blurH, blurV, quad: new FullScreenQuad(blurH), scene, casters,
    };
    // The invisible floor follows the cluster, sits a little below its lowest
    // cube and sinks when cubes go lower, so nothing ever dips through it.
    this.#floorBase = this.#floorY = Math.min(...this.cubes.map((c) => c.home.y)) - HALF - 0.75;
  }

  // Post-processing
  #createComposer() {
    const target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, samples: 4 });
    this.#composer = new EffectComposer(this.renderer, target);
    const fxaa = new FXAAPass();
    fxaa.enabled = false;                                  // a fallback; the last enabled pass draws to screen
    this.#passes = [
      new RenderPass(this.scene, this.camera),
      new ScalableBloomPass(new THREE.Vector2(256, 256), 0.6, 0.4, 1.6),
      new OutputPass(),
      fxaa,
    ];
    for (const pass of this.#passes) this.#composer.addPass(pass);
  }

  #applyFallback(step) {
    const [, bloom, , fxaa] = this.#passes;
    if (step === 'fxaa') {
      for (const target of [this.#composer.renderTarget1, this.#composer.renderTarget2]) {
        target.samples = 0;
        target.dispose();                                  // rebuilt without multisampling on next use
      }
      fxaa.enabled = true;
    } else if (step === 'glass') {
      this.renderer.transmissionResolutionScale = FALLBACK.transmissionScale;
    } else if (step === 'bloom') {
      bloom.setScale(FALLBACK.bloomScale);
    }
    this.#fallbacks.push(step);
  }

  // Compiles the fallbacks' shaders in the background, so switching one in
  // later doesn't stall a frame that is already slow. A program depends on
  // the target it draws into and on the geometry's attributes: the bloom blurs
  // draw into targets, FXAA to the screen, both on a bare full-screen triangle.
  #precompileFallbacks() {
    if (this.#options.quality !== 'auto') return;
    const { renderer, camera } = this;
    const [, bloom, , fxaa] = this.#passes;
    const jobs = [];
    if (this.#steps.includes('bloom')) {
      jobs.push(compileQuads(renderer, bloom.prepareScale(FALLBACK.bloomScale), camera, bloom.renderTargetsHorizontal[0]));
    }
    if (this.#steps.includes('fxaa')) jobs.push(compileQuads(renderer, [fxaa.material], camera, null));
    Promise.all(jobs).catch((err) => console.warn(err));
  }

  // ==========================================================================
  // Interaction
  // ==========================================================================
  #bindEvents() {
    const { canvas } = this;
    const signal = this.#abort.signal;
    canvas.addEventListener('pointerdown', (e) => this.#onPointerDown(e), { signal });
    canvas.addEventListener('pointermove', (e) => this.#onPointerMove(e), { signal });
    canvas.addEventListener('pointerup', (e) => this.#endPointer(e, false), { signal });
    canvas.addEventListener('pointercancel', (e) => this.#endPointer(e, true), { signal });
    canvas.addEventListener('pointerleave', () => { if (!this.#drag.down) this.#pointer.inside = false; }, { signal });
    const keyTarget = this.#options.keyboard === 'page' ? window : canvas;
    keyTarget.addEventListener('keydown', (e) => this.#onKey(e), { signal });
    addEventListener('resize', () => this.resize(), { signal });
    this.#resizeObserver = new ResizeObserver(() => this.resize());
    this.#resizeObserver.observe(this.#container);
    if (this.#options.pauseOffscreen) {
      this.#visibility = new IntersectionObserver((entries) => {
        if (entries.at(-1).isIntersecting) this.resume('offscreen');
        else this.pause('offscreen');
      });
      this.#visibility.observe(canvas);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause('hidden');
        else this.resume('hidden');
      }, { signal });
      if (document.hidden) this.#holds.add('hidden');
    }
  }

  #emit(type, detail = null, cancelable = false) {
    return this.dispatchEvent(new CustomEvent(type, { detail, cancelable }));
  }

  // Click or Space / Enter: toggle, unless a 'press' listener takes over.
  #press(origin) {
    if (this.#emit('press', { origin }, true)) this.toggle(origin);
  }

  #pickCube() {
    this.#raycaster.setFromCamera(this.#pointer.ndc, this.camera);
    const { ray } = this.#raycaster;
    let best = null, bestDist = Infinity;
    for (const c of this.cubes) {
      _inv.copy(c.mesh.matrixWorld).invert();
      _ray.copy(ray).applyMatrix4(_inv);
      if (_ray.intersectBox(LOCAL_BOX, _hit)) {
        _hit.applyMatrix4(c.mesh.matrixWorld);
        const d = _hit.distanceToSquared(ray.origin);
        if (d < bestDist) { bestDist = d; best = c; _hitPoint.copy(_hit); }
      }
    }
    return best;
  }

  #setPointer(e) {
    const rect = this.canvas.getBoundingClientRect();
    const pointer = this.#pointer;
    pointer.type = e.pointerType || 'mouse';
    pointer.px.set(e.clientX, e.clientY);
    pointer.ndc.set(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
    pointer.inside = true;
  }

  #onPointerDown(e) {
    if (e.button !== 0) return;
    this.#setPointer(e);
    if (this.#options.keyboard === 'page') this.canvas.focus({ preventScroll: true });
    const drag = this.#drag;
    drag.down = true; drag.active = false; drag.id = e.pointerId;
    drag.startX = drag.lastX = e.clientX; drag.startY = drag.lastY = e.clientY;
    drag.startTime = performance.now();
    drag.samples.length = 0;
    this.#spin.resetting = false;
    this.canvas.setPointerCapture(e.pointerId);
    if (this.#pointer.type !== 'mouse') this.#pointer.pokeTime = 0.35;   // touch: poke the cubes under the finger
  }

  #onPointerMove(e) {
    this.#setPointer(e);
    const drag = this.#drag, spin = this.#spin;
    if (!drag.down || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.lastX, dy = e.clientY - drag.lastY;
    if (!drag.active && Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) > 6) {
      drag.active = true;
      this.canvas.classList.add('dragging');
    }
    if (drag.active) {
      spin.yaw += dx * SPIN.perPixel;
      spin.pitch = clamp(spin.pitch + dy * SPIN.perPixel, -SPIN.pitchLimit, SPIN.pitchLimit);
      spin.vYaw = spin.vPitch = 0;
      const now = performance.now();
      drag.samples.push({ dx, dy, t: now });
      while (drag.samples.length && now - drag.samples[0].t > 100) drag.samples.shift();
    }
    drag.lastX = e.clientX; drag.lastY = e.clientY;
  }

  #endPointer(e, cancelled) {
    const drag = this.#drag, spin = this.#spin;
    if (!drag.down || e.pointerId !== drag.id) return;
    drag.down = false;
    this.canvas.classList.remove('dragging');
    if (this.canvas.hasPointerCapture(e.pointerId)) this.canvas.releasePointerCapture(e.pointerId);
    if (drag.active) {
      // Release with the velocity of the last ~100 ms of movement.
      const now = performance.now();
      const recent = drag.samples.filter((s) => now - s.t < 100);
      if (recent.length) {
        const span = Math.max((now - recent[0].t) / 1000, 1 / 60);
        const sx = recent.reduce((a, s) => a + s.dx, 0), sy = recent.reduce((a, s) => a + s.dy, 0);
        spin.vYaw = clamp((sx * SPIN.perPixel) / span, -SPIN.maxVelocity, SPIN.maxVelocity);
        spin.vPitch = clamp((sy * SPIN.perPixel) / span, -SPIN.maxVelocity, SPIN.maxVelocity);
      }
      this.#emit('rotate');
    } else if (!cancelled && performance.now() - drag.startTime < 500) {
      const hit = this.#pickCube();
      this.#press(hit ? this.group.worldToLocal(_hitPoint.clone()) : null);
    }
    drag.active = false;
    if (this.#pointer.type !== 'mouse') this.#pointer.inside = false;
  }

  #onKey(e) {
    if (e.target !== this.canvas && e.target !== document.body) return;
    const spin = this.#spin;
    const turn = (yaw, pitch) => {
      spin.vYaw += yaw;
      spin.vPitch += pitch;
      spin.resetting = false;
      this.#emit('rotate');
    };
    if (e.code === 'Space' || e.code === 'Enter') this.#press(null);
    else if (e.code === 'ArrowLeft') turn(-1.6, 0);
    else if (e.code === 'ArrowRight') turn(1.6, 0);
    else if (e.code === 'ArrowUp') turn(0, -1.2);
    else if (e.code === 'ArrowDown') turn(0, 1.2);
    else if (e.code === 'KeyR') this.resetView();
    else return;
    // Space and Enter would scroll or click; arrows stay with the page unless the canvas has focus.
    if (e.target === this.canvas || e.code === 'Space' || e.code === 'Enter') e.preventDefault();
  }

  // ==========================================================================
  // Moves between layouts
  // ==========================================================================
  // Each cube keeps a queue of moves (see updatePose). via: a layout to sweep
  // through on the way.
  #sendTo(layout, { delay = 0, stagger = () => 0, dur, ease, arc = null, onArrive = null, via = null }) {
    const t0 = this.#state.time + delay;
    const pose = (l, i) => ({
      pos: l.poses[i].pos, quat: l.poses[i].quat, drift: l.drift, spread: l.spread, icon: l.icon,
      phase: l.order ? l.order[i] * 0.35 : 0,
    });
    this.cubes.forEach((c, i) => {
      c.queue.length = 0;
      c.queue.push({
        start: t0 + stagger(c, i), dur, ease, onArrive, from: null,
        arc: arc ? arc(c, i) : null,
        via: via ? pose(via, i) : null,
        to: pose(layout, i),
      });
    });
  }

  #expandCubes(origin) {
    this.#sendTo(this.#layouts.expanded, { stagger: staggerFrom(origin), dur: EXPAND.durOut, ease: EASE_SPREAD });
  }

  // Sends the cubes into a layout, optionally sweeping through another on the
  // way: they lock back into the cluster, or swoop towards the viewer and land
  // in an icon.
  #formLayout(layout, { delay = 0, origin = null, via = null, dur } = {}) {
    if (layout === this.#layouts.home) {
      this.#sendTo(layout, { delay, via, stagger: staggerFrom(origin), dur: dur ?? EXPAND.durIn, ease: EASE, onArrive: lockIntoPlace });
      return;
    }
    const seq = this.#seq;
    this.#sendTo(layout, {
      delay, via, dur: dur ?? seq.formDur, ease: EASE,
      stagger: (c, i) => layout.order[i] * seq.formStagger + c.seed * 0.05,
      arc: (c) => ICON_NORMAL.clone().multiplyScalar(0.5 + 1.1 * c.seed),   // swoop towards the viewer
      onArrive: settleIntoIcon,
    });
  }

  #later(delay, fn) {
    this.#timeline.push({ at: this.#state.time + delay, fn });
  }

  #runTimeline(t) {
    const timeline = this.#timeline;
    for (let i = 0; i < timeline.length;) {
      if (t >= timeline[i].at) timeline.splice(i, 1)[0].fn();
      else i++;
    }
  }

  // ==========================================================================
  // Animation
  // ==========================================================================
  // Finishes building in steps, so the page stays responsive while it does:
  // the studio, then the fibers, then every program the first frame needs,
  // compiled in the background where the GPU can. Then the frames start.
  async #start() {
    const { renderer, camera } = this;
    try {
      await yieldToMain();                                 // ends the constructor's task
      await renderStudio(renderer, this.#studio, this.#abort.signal);
      if (this.#disposed) return;
      this.scene.environment = this.#studio.env;           // before compiling: programs depend on it
      await this.#buildFibers();
      if (this.#disposed) return;
      this.#updateCamera(1);
      this.#updateCluster(0, this.#state.time);
      this.#updateCubes(0, this.#state.time);
      this.scene.updateMatrixWorld();
      this.#updateFloor(10);
      this.#updateGlow(this.#state.time);
      this.#updateReflections();
      this.#syncShadowCasters();
      // The scene only ever draws into targets: the composer's and the glass's.
      const shadow = this.#shadow;
      const [, bloom] = this.#passes;
      await Promise.all([
        compileFor(renderer, this.scene, camera, this.#composer.readBuffer),
        compileFor(renderer, shadow.scene, shadow.camera, shadow.rt),
        compileQuads(renderer, [shadow.blurH, shadow.blurV], camera, shadow.rt),
        compileQuads(renderer, [bloom.materialHighPassFilter, ...bloom.separableBlurMaterials,
          bloom.compositeMaterial, bloom.blendMaterial], camera, bloom.renderTargetBright),
      ]);
    } catch (err) {
      console.warn(err);
    }
    if (this.#disposed) return;
    this.#started = true;
    this.#run();
    this.#precompileFallbacks();
  }

  // Builds the fibers' geometry from the plans #createCubes made, yielding
  // to the page every few milliseconds.
  async #buildFibers() {
    let slice = performance.now();
    for (const cube of this.#glassCubes) {
      const fibers = buildFibers(cube.fiberPlan, this.#geometries.tip);
      cube.strands.geometry.dispose();
      cube.tips.geometry.dispose();
      cube.strands.geometry = cube.filaments.geometry = fibers.strands;
      cube.tips.geometry = fibers.tips;
      cube.fiberPlan = null;
      if (performance.now() - slice > 8) {
        await yieldToMain();
        if (this.#disposed) return;
        slice = performance.now();
      }
    }
  }

  // Starts the frames, unless something is holding them (see pause).
  #run() {
    if (!this.#canRun()) return;
    this.#last = performance.now();                        // no jump in time...
    this.#scroll.y = scrollY;                              // ...or scroll speed
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #canRun() {
    return this.#started && !this.#disposed && !this.#holds.size && !this.#raf;
  }

  #frame(now) {
    this.#raf = 0;                                         // this frame's request is spent
    const rawDt = (now - this.#last) / 1000;
    const dt = Math.min(rawDt, 1 / 20);
    this.#last = now;
    const state = this.#state;
    state.time = this.#options.time ?? state.time + dt;
    const t = state.time;

    this.#runTimeline(t);
    this.#updateSpin(dt);
    this.#updateScroll(dt);
    this.#updateCamera(dt);
    this.#updateCluster(dt, t);
    this.#updateHover(dt);
    this.#updateTweens(t);
    this.#updateCubes(dt, t);
    this.scene.updateMatrixWorld();
    this.#updateFloor(dt);
    this.#shadow.group.updateMatrixWorld();
    this.#updateGlow(t);

    this.#updateReflections();
    this.#syncShadowCasters();
    this.#renderContactShadow();
    this.#composer.render(dt);

    // Where frames run slow for a while, step down: first the fallbacks, one
    // at a time, then the resolution. One long pause counts as a single missed
    // frame, so it can't hold the average up on its own.
    this.#frameCount++;
    const ladder = this.#options.quality === 'auto' && this.#options.time == null && this.#steps.length > 0;
    const adaptive = this.#fixedPixelRatio === null;
    if (this.#frameCount > 90 && rawDt < 0.25 && (ladder || adaptive)) {
      this.#avgFrame += (Math.min(rawDt, 1 / 30) - this.#avgFrame) * 0.05;
      if (ladder) {
        if (this.#frameCount > this.#settleUntil) {
          this.#fallbackSlow = this.#avgFrame > 1.05 / FALLBACK.minFps ? this.#fallbackSlow + rawDt : 0;
          if (this.#fallbackSlow > FALLBACK.after) {
            this.#applyFallback(this.#steps.shift());
            this.#fallbackSlow = 0;
            this.#settleUntil = this.#frameCount + 15;     // targets rebuild, the average catches up
          }
        }
      } else if (adaptive) {
        this.#slowTime = this.#avgFrame > 1 / 40 ? this.#slowTime + rawDt : 0;
        if (this.#slowTime > 1.5 && this.#pixelRatio > 1) {
          this.#pixelRatio = Math.max(1, this.#pixelRatio - 0.25);
          this.#slowTime = 0;
          this.resize();
        }
      }
    }
    if (this.#frameCount === 3) {
      this.#ready = true;
      this.canvas.classList.add('ready');
      this.#emit('ready');
    }
    if (this.#canRun()) this.#raf = requestAnimationFrame(this.#tick);   // unless a listener paused or disposed it
  }

  #updateSpin(dt) {
    if (this.#drag.active) return;
    const spin = this.#spin;
    if (spin.resetting) {
      // Critically damped return to the reference view.
      const w = 7;
      spin.vYaw += (-w * w * spin.yaw - 2 * w * spin.vYaw) * dt;
      spin.vPitch += (-w * w * spin.pitch - 2 * w * spin.vPitch) * dt;
      if (Math.abs(spin.yaw) + Math.abs(spin.pitch) < 1e-3) {
        spin.yaw = spin.pitch = spin.vYaw = spin.vPitch = 0;
        spin.resetting = false;
      }
    } else {
      const decay = Math.exp(-SPIN.damping * dt);
      spin.vYaw *= decay;
      spin.vPitch *= decay;
    }
    spin.yaw += spin.vYaw * dt;
    spin.pitch += spin.vPitch * dt;
    if (Math.abs(spin.pitch) > SPIN.pitchLimit) {
      spin.pitch = Math.sign(spin.pitch) * SPIN.pitchLimit;
      spin.vPitch *= -0.25;
    }
  }

  // Scrolling turns the cluster a little, and a quick scroll drags the cubes along.
  #updateScroll(dt) {
    if (!this.#options.scrollEffects) return;
    const s = this.#scroll;
    const vh = Math.max(innerHeight, 1);
    const y = scrollY;
    const vel = dt > 0 ? clamp((y - s.y) / vh / dt, -3, 3) : 0;   // viewports per second
    s.y = y;
    if (motionQuery.matches) return;
    s.vel += (vel - s.vel) * (1 - Math.exp(-dt * 5));
    s.turn += (Math.sin((y / vh) * 1.1) * SCROLL.turn - s.turn) * (1 - Math.exp(-dt * 4));
  }

  #updateHover(dt) {
    const pointer = this.#pointer, drag = this.#drag;
    pointer.pokeTime = Math.max(0, pointer.pokeTime - dt);
    const speed = pointer.px.distanceTo(pointer.lastPx) / Math.max(dt, 1e-3);
    pointer.lastPx.copy(pointer.px);
    pointer.speed += (speed - pointer.speed) * (1 - Math.exp(-dt * 10));

    const decay = Math.exp(-DISTURB.decay * dt);
    for (const c of this.cubes) c.excite *= decay;

    const canHover = pointer.inside && !drag.active && (pointer.type === 'mouse' || pointer.pokeTime > 0 || drag.down);
    const hit = canHover ? this.#pickCube() : null;
    this.canvas.classList.toggle('over', !!hit);
    if (hit && !this.#hovering) this.#emit('disturb');
    this.#hovering = !!hit;
    if (!hit) return;

    // Work in the cluster's local frame so offsets follow its rotation.
    this.group.worldToLocal(_hoverLocal.copy(_hitPoint));
    _invQ.copy(this.group.quaternion).invert();
    _toCam.copy(camDir).applyQuaternion(_invQ);
    const strength = clamp(0.5 + pointer.speed / 1600, 0.5, 1.25);
    const r2 = DISTURB.radius * DISTURB.radius;
    const blend = 1 - Math.exp(-dt * 12);
    for (const c of this.cubes) {
      const w = Math.exp(-c.rest.distanceToSquared(_hoverLocal) / r2);
      if (w < 0.02) continue;
      c.excite = Math.max(c.excite, w * strength);
      // Lift the patch towards the viewer and part it around the pointer.
      _away.subVectors(c.rest, _hoverLocal);
      _away.addScaledVector(_toCam, -_away.dot(_toCam));
      const lateral = Math.min(_away.length() / DISTURB.radius, 1);
      if (_away.lengthSq() > 1e-6) _away.normalize();
      _v.copy(c.rest).normalize().multiplyScalar(0.45)
        .addScaledVector(_away, 0.9 * lateral)
        .addScaledVector(_toCam, 0.75)
        .normalize();
      c.disturbDir.lerp(_v, blend).normalize();
    }
  }

  #updateTweens(t) {
    for (const tw of this.#tweens) {
      if (t < tw.start) continue;
      const { custom } = tw.cube;
      if (!tw.from) {
        // Start from wherever the cube is when the tween begins.
        const r = custom.rotation;
        tw.from = { position: custom.position.clone(), rotation: new THREE.Vector3(r.x, r.y, r.z), scale: custom.scale };
        tw.target = {
          position: tw.to.position ? toVector3(tw.to.position) : tw.from.position,
          rotation: tw.to.rotation ? toVector3(tw.to.rotation) : tw.from.rotation,
          scale: tw.to.scale ?? tw.from.scale,
        };
      }
      const u = clamp((t - tw.start) / tw.duration, 0, 1);
      const k = tw.ease(u);
      custom.position.lerpVectors(tw.from.position, tw.target.position, k);
      _tweenRot.lerpVectors(tw.from.rotation, tw.target.rotation, k);
      custom.rotation.set(_tweenRot.x, _tweenRot.y, _tweenRot.z);
      custom.scale = tw.from.scale + (tw.target.scale - tw.from.scale) * k;
      if (u >= 1) { this.#tweens.delete(tw); tw.resolve(tw.cube); }
    }
    return this.#tweens.size;
  }

  #updateCubes(dt, t) {
    const anchor = this.#anchor;
    let spread = 0;
    let motion = 0;
    const still = motionQuery.matches;
    // Cubes trail behind a travelling cluster, the back of the swarm the most.
    const trailing = Math.abs(anchor.velocity) > 1e-3;
    if (trailing) _trail.copy(camRight).applyQuaternion(_invQ.copy(this.group.quaternion).invert());
    const heading = Math.sign(anchor.velocity);
    const scrollVel = this.#scroll.vel;
    for (const c of this.cubes) {
      if (updatePose(c, t)) motion += 1;
      const pose = c.pose;
      spread += pose.spread;

      // Disturbance springs.
      _v.copy(c.disturbDir).multiplyScalar(c.excite * DISTURB.amp);
      c.offsetVel.addScaledVector(_v.sub(c.offset), DISTURB.stiffness * dt);
      c.offsetVel.multiplyScalar(Math.exp(-DISTURB.damping * dt));
      c.offset.addScaledVector(c.offsetVel, dt);
      const tiltTarget = c.excite * DISTURB.tilt;
      c.tiltVel += (tiltTarget - c.tilt) * DISTURB.stiffness * dt;
      c.tiltVel *= Math.exp(-DISTURB.damping * dt);
      c.tilt += c.tiltVel * dt;
      motion += c.offsetVel.lengthSq() + c.tiltVel * c.tiltVel;

      // Compose: pose + custom layer (= rest), then disturbance, drift, trail and scroll.
      c.rest.copy(pose.pos).add(c.custom.position);
      const p = c.group.position.copy(c.rest).add(c.offset);
      const drift = still ? 0 : pose.drift;
      if (drift > 0) {
        const s = c.seed * 40;
        p.x += Math.sin(t * 0.53 + s) * 0.07 * drift;
        p.y += Math.sin(t * 0.71 + s * 1.3) * 0.1 * drift;
        p.z += Math.sin(t * 0.61 + s * 0.7) * 0.07 * drift;
      }
      if (trailing) {
        const ahead = clamp(pose.pos.dot(_trail) / 6, -1, 1) * heading;   // 1 at the front of the swarm
        const lag = 0.0036 + 0.0108 * (1 - ahead) + 0.006 * c.seed;      // seconds; halve it if the trip gets twice as fast
        p.addScaledVector(_trail, -anchor.velocity * lag);
      }
      if (scrollVel) p.y += scrollVel * SCROLL.jelly * (0.4 + 0.6 * c.seed);

      const q = c.group.quaternion.copy(pose.quat);
      if (drift > 0) {
        _e.set(Math.sin(t * 0.4 + c.seed * 9) * 0.08 * drift, Math.sin(t * 0.33 + c.seed * 5) * 0.1 * drift, 0);
        q.multiply(_q.setFromEuler(_e));
      }
      if (c.tilt !== 0) q.multiply(_q.setFromAxisAngle(c.tiltAxis, c.tilt));
      if (c.custom.rotation.x || c.custom.rotation.y || c.custom.rotation.z) q.multiply(_q.setFromEuler(c.custom.rotation));
      c.group.scale.setScalar(c.custom.scale);
    }
    this.#state.spread = spread / this.cubes.length;
    return motion;
  }

  #updateGlow(t) {
    for (const c of this.#glassCubes) {
      // In an icon the glass glows evenly and the pulse ripples out from its centre.
      const icon = c.pose.icon;
      const pulse = pulseAt(t, c.pulsePhase + (c.iconPhase - c.pulsePhase) * icon);
      const glow = c.glowLevel + (ICON.glow - c.glowLevel) * icon;
      const level = pulse * glow * (1 + 1.6 * c.excite);
      const body = c.isCore ? 6 : 1;
      c.fiberMaterial.uniforms.uIntensity.value = GLOW.fiber * level;
      c.fiberMaterial.uniforms.uTime.value = t;
      c.filamentMaterial.uniforms.uIntensity.value = GLOW.filament * level;
      c.filamentMaterial.uniforms.uTime.value = t;
      c.haloMaterial.uniforms.uIntensity.value = GLOW.halo * level;
      c.tipMaterial.color.copy(this.#glow.tip).multiplyScalar(GLOW.tip * level);
      c.material.emissiveIntensity = GLOW.glass * level * (body + (ICON.core - body) * icon);
      c.reflectGlow = level;
    }
    const avg = pulseAt(t, 0.6);
    for (const light of this.#coreLights) light.intensity = GLOW.coreLight * avg * (1 - 0.6 * this.#state.spread);
  }

  // Camera distance that fits the given bounds into the cluster's slot, and how
  // far (in NDC) its centre can sit from the middle of the canvas.
  #frameFor(bounds) {
    const layout = this.#layout;
    const tanV = Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2));
    const tanH = tanV * this.camera.aspect;
    const fitH = bounds.halfH / (this.#band.half * tanV);
    if (this.#compact) return { distance: Math.max(fitH, bounds.halfW / (layout.compactSlot * tanH)), tanH, room: 0 };
    const distance = Math.max(fitH, bounds.halfW / (layout.slot * tanH));
    return { distance, tanH, room: clamp(1 - layout.margin - bounds.halfW / (distance * tanH), 0, layout.anchor) };
  }

  // The camera eases towards whatever the current layout needs to fit.
  #updateCamera(dt) {
    const target = this.#layouts[this.#view.frame].bounds;
    const fit = this.#fit;
    const k = dt >= 1 ? 1 : 1 - Math.exp(-dt * 5.8);
    fit.halfH += (target.halfH - fit.halfH) * k;
    fit.halfW += (target.halfW - fit.halfW) * k;
    Object.assign(this.#view, this.#frameFor(fit));
    this.camera.position.copy(camDir).multiplyScalar(this.#view.distance);
    this.camera.lookAt(0, 0, 0);
  }

  // Places the cluster on its side of the canvas. It is turned to face the
  // camera as if it were centred, so the reference look holds on either side;
  // icons also turn a little towards the middle.
  #updateCluster(dt, t) {
    const view = this.#view, anchor = this.#anchor, group = this.group;
    const before = anchor.value;
    if (t >= anchor.start) {
      const u = clamp((t - anchor.start) / anchor.dur, 0, 1);
      anchor.value = anchor.from + (anchor.to - anchor.from) * EASE(u);
    }
    const scale = view.room * view.distance * view.tanH;              // world units per unit of anchor
    anchor.velocity = dt > 0 ? ((anchor.value - before) / dt) * scale : 0;
    const x = anchor.value * scale;
    group.position.copy(camRight).multiplyScalar(x);
    group.position.y = motionQuery.matches ? 0 : Math.sin(t * 0.55) * 0.07;

    const ease = 1 - Math.exp(-dt * 5);
    view.turn += ((ICONS[view.target] ? 1 : 0) - view.turn) * ease;
    view.turnSide += (view.side - view.turnSide) * ease;
    const { turn, sway } = this.#layout;
    const swing = motionQuery.matches ? 0 : sway * Math.sin(t * 0.45);
    const facing = -Math.atan2(x, view.distance * camLevel);
    const toMiddle = this.#compact ? 0 : -view.turnSide * (turn + swing) * view.turn;
    qYaw.setFromAxisAngle(UP, this.#spin.yaw + facing + toMiddle + this.#scroll.turn);
    qPitch.setFromAxisAngle(camRight, this.#spin.pitch);
    group.quaternion.multiplyQuaternions(qPitch, qYaw);
    this.#backdrop.material.uniforms.uCenter.value = anchor.value * view.room * 0.6;
  }

  #updateFloor(dt) {
    let lowest = Infinity;
    for (const c of this.cubes) lowest = Math.min(lowest, c.mesh.matrixWorld.elements[13]);
    const target = Math.min(this.#floorBase, lowest - 0.95);
    const rate = target < this.#floorY ? 6 : 1.5;
    this.#floorY += (target - this.#floorY) * (1 - Math.exp(-dt * rate));
    this.#shadow.group.position.set(this.group.position.x, this.#floorY, this.group.position.z);
  }

  // ==========================================================================
  // Reflections and contact shadow
  // ==========================================================================
  // Hands every cube's box to the chrome shader. The rotation is built from
  // the hierarchy (cluster, then cube) rather than read back from the matrix,
  // which has none left once a cube is scaled to nothing.
  #updateReflections() {
    const boxes = this.#reflect.boxes.value;
    for (const c of this.cubes) {
      const o = c.index * 8;
      const e = c.mesh.matrixWorld.elements;
      const size = HALF * Math.abs(c.group.scale.x);
      const q = _q.multiplyQuaternions(this.group.quaternion, c.group.quaternion);
      const sign = q.w < 0 ? -1 : 1;
      const hidden = size < 1e-4;
      boxes[o] = e[12];
      boxes[o + 1] = hidden ? -1e4 : e[13];
      boxes[o + 2] = e[14];
      boxes[o + 3] = hidden ? 0 : size;
      boxes[o + 4] = q.x * sign;
      boxes[o + 5] = q.y * sign;
      boxes[o + 6] = q.z * sign;
      boxes[o + 7] = c.type === 'g' ? REFLECT.glow * c.reflectGlow : c.type === 'c' ? -2 : -1;
    }
    const floor = this.#shadow.group.position;
    this.#reflect.floorInfo.value.set(floor.x, floor.y, floor.z, 0);
  }

  #syncShadowCasters() {
    const { casters } = this.#shadow;
    for (const c of this.cubes) casters.setMatrixAt(c.index, c.mesh.matrixWorld);
    casters.instanceMatrix.needsUpdate = true;
  }

  #blurShadow(amount) {
    const { renderer } = this;
    const { rt, blurRT, blurH, blurV, quad } = this.#shadow;
    quad.material = blurH;
    blurH.uniforms.tDiffuse.value = rt.texture;
    blurH.uniforms.h.value = amount / SHADOW.res;
    renderer.setRenderTarget(blurRT);
    quad.render(renderer);
    quad.material = blurV;
    blurV.uniforms.tDiffuse.value = blurRT.texture;
    blurV.uniforms.v.value = amount / SHADOW.res;
    renderer.setRenderTarget(rt);
    quad.render(renderer);
  }

  #renderContactShadow() {
    const { renderer } = this;
    const shadow = this.#shadow;
    shadow.caster.uniforms.uFloor.value = shadow.group.position.y;
    renderer.getClearColor(_clearColor);
    const clearAlpha = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 0);
    renderer.setRenderTarget(shadow.rt);
    renderer.clear();
    renderer.render(shadow.scene, shadow.camera);
    this.#blurShadow(SHADOW.blur);
    this.#blurShadow(SHADOW.blur * 0.55);
    renderer.setRenderTarget(null);
    renderer.setClearColor(_clearColor, clearAlpha);
  }
}
